import { arrayValue, isArray } from '../packages/array.js';
import type {
  ToolDefinition,
  ToolId,
  ToolRegistryCurrentPointer,
  ToolVersionPointer,
} from '@hephesapla/tool-registry';
import {
  canonicalRegistryJson,
  canonicalUnsignedToolRegistryPointer,
  getBrowserBundledPilotTools,
} from '@hephesapla/tool-registry/browser-contract';
import nacl from 'tweetnacl';
import { assertRegistryContract } from './registry-contract.js';

const historicalToolIds = [
  'percentage',
  'salary',
  'hgs-toll',
  'unit-weight',
  'unit-area',
  'unit-volume',
  'unit-speed',
  'unit-temperature',
  'unit-length',
  'unit-data-size',
  'unit-time',
  'geometry',
  'prime-number',
  'gcd-lcm',
  'power-root',
  'quadratic-equation',
  'fraction',
  'ratio-proportion',
  'pythagorean',
  'alan-fire',
  'alci-siva-macun',
  'boya',
  'derz-dolgu',
  'duvar-kagidi-rulo',
  'fayans-seramik',
  'curtain-fabric',
  'hali-metrekare',
  'kutu-paket-sayisi',
  'laminat-parke',
  'oda-metrekare',
  'supurgelik-metre',
  'zebra-stor-perde-olcu',
  'age',
  'date-difference',
  'duration',
  'date-adjustment',
  'iso-week',
  'relative-grade',
  'report-card-average',
  'grade-average',
  'required-final-score',
  'lpg-payback',
  'fuel-consumption',
  'profit-margin',
  'basal-metabolic-rate',
  'historical-ideal-weight-reference',
  'compound-interest',
  'inflation-value',
  'housing-loan',
  'consumer-loan',
  'depreciation-plan',
  'discount',
  'rent-increase',
  'pos-commission',
] as const satisfies readonly ToolId[];

const sourcePreparedToolIds = [
  'vehicle-purchase-cost',
  'vehicle-inspection-fee',
  'vehicle-excise-tax',
  'natural-gas-bill',
  'electricity-bill',
  'motor-vehicle-tax',
  'water-bill',
  'title-deed-fee',
  'traffic-fine',
  'valuable-housing-tax',
  'income-tax',
  'unemployment-benefit',
  'severance-notice-pay',
  'rental-income-tax',
  'early-loan-closure',
  'credit-card-minimum-payment',
  'deposit-return',
  'vehicle-loan',
  'inheritance-gift-tax',
  'open-high-school-obp',
  'open-education-gpa',
  'ales-score',
  'aof-final-pass-score',
  'scholarship-iokbs-score',
  'dgs-score',
  'dgs-preference-ranking',
  'kpss-score',
  'lgs-score',
  'driving-license-exam',
  'obp',
  'honor-certificate',
  'university-preference-ranking',
  'yds-yokdil-score',
  'yks-score',
  'military-service-countdown',
  'countdown',
  'hijri-gregorian',
  'business-days',
  'waist-hip-ratio',
  'pregnancy-dating',
  'daily-calorie-need',
  'daily-water-reference',
  'adult-bmi',
  'historical-body-fat',
  'stamp-tax',
  'property-tax',
  'advance-tax',
  'late-legal-interest',
  'employer-cost',
  'vat',
  'vat-withholding',
  'corporate-tax',
  'self-employment-receipt',
  'withholding-tax',
] as const satisfies readonly ToolId[];
type SourcePreparedToolId = (typeof sourcePreparedToolIds)[number];

const toolIds = [
  ...historicalToolIds,
  ...sourcePreparedToolIds,
] as const satisfies readonly ToolId[];
type BrowserToolId = (typeof toolIds)[number];
const unitToolIds = [
  'unit-weight',
  'unit-area',
  'unit-volume',
  'unit-speed',
  'unit-temperature',
  'unit-length',
  'unit-data-size',
  'unit-time',
] as const satisfies readonly ToolId[];
type UnitToolId = (typeof unitToolIds)[number];
const mathToolIds = [
  'geometry', 'prime-number', 'gcd-lcm', 'power-root',
  'quadratic-equation', 'fraction', 'ratio-proportion', 'pythagorean',
] as const satisfies readonly ToolId[];
type MathToolId = (typeof mathToolIds)[number];
const homeToolIds = [
  'alan-fire', 'alci-siva-macun', 'boya', 'derz-dolgu',
  'duvar-kagidi-rulo', 'fayans-seramik', 'curtain-fabric',
  'hali-metrekare', 'kutu-paket-sayisi', 'laminat-parke',
  'oda-metrekare', 'supurgelik-metre', 'zebra-stor-perde-olcu',
] as const satisfies readonly ToolId[];
type HomeToolId = (typeof homeToolIds)[number];
const dateTimeToolIds = [
  'age', 'date-difference', 'duration', 'date-adjustment', 'iso-week',
] as const satisfies readonly ToolId[];
type DateTimeToolId = (typeof dateTimeToolIds)[number];
const educationToolIds = [
  'relative-grade', 'report-card-average', 'grade-average', 'required-final-score',
] as const satisfies readonly ToolId[];
type EducationToolId = (typeof educationToolIds)[number];
const everydayHealthToolIds = [
  'lpg-payback', 'fuel-consumption',
  'profit-margin', 'basal-metabolic-rate', 'historical-ideal-weight-reference',
] as const satisfies readonly ToolId[];
type EverydayHealthToolId = (typeof everydayHealthToolIds)[number];
const financeBusinessToolIds = [
  'compound-interest', 'inflation-value', 'housing-loan', 'consumer-loan',
  'depreciation-plan', 'discount', 'rent-increase', 'pos-commission',
] as const satisfies readonly ToolId[];
type FinanceBusinessToolId = (typeof financeBusinessToolIds)[number];
const sourceOptionalEverydayHealthToolIds = new Set<EverydayHealthToolId>([
  'lpg-payback', 'fuel-consumption', 'profit-margin',
]);
const maximumPointerBytes = 128 * 1024;
const maximumToolBytes = 1024 * 1024;
const maximumValidityMs = 45 * 24 * 60 * 60 * 1000;
const maximumActivationClockSkewMs = 5 * 60 * 1000;
// Kullanıcının 100.000 TL asgari ÖTV güncellemesiyle iki araç 0.4.1 kapısına
// yükseldi. Signed snapshot bu bundle tarafından çalıştırılacağı için sürüm
// kapısı görünür web release sürümüyle aynı tutulur.
const supportedWebClientVersion = '0.4.1';

const sourcePreparedContracts: Readonly<Partial<Record<SourcePreparedToolId, readonly [string, string]>>> = Object.fromEntries<readonly [string, string]>(
  sourcePreparedToolIds.map((toolId) => {
    const bundled = getBrowserBundledPilotTools().find((tool) => tool.toolId === toolId);
    if (!bundled) throw new Error(`${toolId} paketli source-prepared sözleşmesi bulunamadı.`);
    return [toolId, [bundled.engineId, bundled.config.kind] as const];
  }),
);

const contracts: Readonly<Partial<Record<BrowserToolId, readonly [string, string]>>> = {
  percentage: ['percentage-v1', 'percentage-presentation-v1'],
  salary: ['salary-annual-average-v1', 'salary-annual-rules-v1'],
  'hgs-toll': ['hgs-fixed-toll-v1', 'hgs-fixed-tariffs-v1'],
  'unit-weight': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-area': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-volume': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-speed': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-temperature': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-length': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-data-size': ['unit-converter-v1', 'unit-converter-v1'],
  'unit-time': ['unit-converter-v1', 'unit-converter-v1'],
  geometry: ['geometry-v1', 'geometry-static-v1'],
  'prime-number': ['prime-number-v1', 'prime-number-static-v1'],
  'gcd-lcm': ['gcd-lcm-v1', 'gcd-lcm-static-v1'],
  'power-root': ['power-root-v2', 'power-root-static-v2'],
  'quadratic-equation': ['quadratic-equation-v1', 'quadratic-equation-static-v1'],
  fraction: ['fraction-v1', 'fraction-static-v1'],
  'ratio-proportion': ['ratio-proportion-v1', 'ratio-proportion-static-v1'],
  pythagorean: ['pythagorean-v1', 'pythagorean-static-v1'],
  'alan-fire': ['home-renovation-v1', 'home-renovation-v1'],
  'alci-siva-macun': ['home-renovation-v1', 'home-renovation-v1'],
  boya: ['home-renovation-v1', 'home-renovation-v1'],
  'derz-dolgu': ['home-renovation-v1', 'home-renovation-v1'],
  'duvar-kagidi-rulo': ['home-renovation-v1', 'home-renovation-v1'],
  'fayans-seramik': ['home-renovation-v1', 'home-renovation-v1'],
  'curtain-fabric': ['curtain-fabric-v2', 'curtain-fabric-v2'],
  'hali-metrekare': ['home-renovation-v1', 'home-renovation-v1'],
  'kutu-paket-sayisi': ['home-renovation-v1', 'home-renovation-v1'],
  'laminat-parke': ['home-renovation-v1', 'home-renovation-v1'],
  'oda-metrekare': ['home-renovation-v1', 'home-renovation-v1'],
  'supurgelik-metre': ['home-renovation-v1', 'home-renovation-v1'],
  'zebra-stor-perde-olcu': ['home-renovation-v1', 'home-renovation-v1'],
  age: ['age-v1', 'calendar-date-v1'],
  'date-difference': ['date-difference-v1', 'calendar-date-v1'],
  duration: ['duration-v1', 'duration-v1'],
  'date-adjustment': ['date-adjustment-v1', 'date-adjustment-v1'],
  'iso-week': ['iso-week-v1', 'calendar-date-v1'],
  'relative-grade': ['relative-system-grade-v1', 'relative-system-grade-v1'],
  'report-card-average': ['report-card-average-v1', 'report-card-average-v1'],
  'grade-average': ['grade-average-v2', 'grade-average-v2'],
  'required-final-score': ['required-final-score-v2', 'weighted-assessment-v2'],
  'lpg-payback': ['lpg-payback-v1', 'lpg-payback-v1'],
  'fuel-consumption': ['fuel-consumption-v1', 'fuel-consumption-v1'],
  'profit-margin': ['profit-margin-v2', 'profit-margin-v2'],
  'basal-metabolic-rate': ['basal-metabolic-rate-v1', 'basal-metabolic-rate-v1'],
  'historical-ideal-weight-reference': [
    'historical-ideal-weight-reference-v1',
    'historical-ideal-weight-reference-v1',
  ],
  'compound-interest': ['compound-interest-v1', 'compound-interest-v1'],
  'inflation-value': ['inflation-value-v1', 'inflation-value-v1'],
  'housing-loan': ['housing-loan-v1', 'housing-loan-v1'],
  'consumer-loan': ['consumer-loan-v1', 'consumer-loan-v1'],
  'depreciation-plan': ['depreciation-plan-v1', 'depreciation-plan-v1'],
  discount: ['discount-v1', 'discount-v1'],
  'rent-increase': ['rent-increase-v1', 'rent-increase-v1'],
  'pos-commission': ['pos-commission-v1', 'pos-commission-v1'],
  ...sourcePreparedContracts,
};

interface MathBrowserContract {
  readonly slug: string;
  readonly path: string;
  readonly sourceIds: readonly string[];
  readonly testVectorKind: string;
  readonly inputIds: readonly string[];
  readonly resultIds: readonly string[];
  readonly configKeys: readonly string[];
  readonly maxAbsoluteInput: number;
}

const mathContracts: Readonly<Record<MathToolId, MathBrowserContract>> = {
  geometry: {
    slug: 'alan-cevre-hacim-hesaplama', path: '/matematik/alan-cevre-hacim-hesaplama',
    sourceIds: ['openstax-geometry'], testVectorKind: 'geometry-oracle-v1',
    inputIds: ['shape', 'dimensions'], resultIds: ['shape', 'dimension', 'primaryMetric', 'primaryValue', 'metrics'],
    configKeys: ['kind', 'maxAbsoluteInput', 'supportedShapes'], maxAbsoluteInput: 1e9,
  },
  'prime-number': {
    slug: 'asal-sayi-hesaplama', path: '/matematik/asal-sayi-hesaplama',
    sourceIds: ['openstax-prime-number'], testVectorKind: 'prime-number-oracle-v1',
    inputIds: ['value'], resultIds: ['value', 'classification', 'factors', 'divisorCount', 'previousPrime', 'nextPrime'],
    configKeys: ['kind', 'maxAbsoluteInput', 'maxInteger'], maxAbsoluteInput: 1e15,
  },
  'gcd-lcm': {
    slug: 'ebob-ekok-hesaplama', path: '/matematik/ebob-ekok-hesaplama',
    sourceIds: ['openstax-gcd-lcm'], testVectorKind: 'gcd-lcm-oracle-v1',
    inputIds: ['values'], resultIds: ['values', 'gcd', 'lcm'],
    configKeys: ['kind', 'maxAbsoluteInput', 'maxInteger', 'minValues', 'maxValues'], maxAbsoluteInput: 1e12,
  },
  'power-root': {
    slug: 'us-ve-kok-hesaplama', path: '/matematik/us-ve-kok-hesaplama',
    // Ayrı faktöriyel aracı kaldırıldı; DLMF kaynağı ve tam sayı sonuçları
    // artık aynı v2 üst/kök sözleşmesinin faktöriyel moduna aittir.
    sourceIds: ['openstax-power-root', 'nist-dlmf-factorial'], testVectorKind: 'power-root-oracle-v2',
    inputIds: ['mode', 'x', 'n'],
    resultIds: ['mode', 'value', 'doubleFactorial', 'digits', 'scientific', 'combination', 'permutation', 'sign'],
    configKeys: ['kind', 'maxAbsoluteInput', 'maxN', 'supportedModes'], maxAbsoluteInput: 1e12,
  },
  'quadratic-equation': {
    slug: 'ikinci-derece-denklem-hesaplama', path: '/matematik/ikinci-derece-denklem-hesaplama',
    sourceIds: ['openstax-quadratic-equation'], testVectorKind: 'quadratic-equation-oracle-v1',
    inputIds: ['a', 'b', 'c'], resultIds: ['a', 'b', 'c', 'discriminant', 'rootType', 'x1', 'x2', 'rootSum', 'rootProduct', 'vertex'],
    configKeys: ['kind', 'maxAbsoluteInput'], maxAbsoluteInput: 1e12,
  },
  fraction: {
    slug: 'kesir-hesaplama', path: '/matematik/kesir-hesaplama',
    sourceIds: ['openstax-fraction'], testVectorKind: 'fraction-oracle-v1',
    inputIds: ['operation', 'numerator1', 'denominator1', 'numerator2', 'denominator2'],
    resultIds: ['operation', 'numerator', 'denominator', 'fraction', 'decimal', 'mixed'],
    configKeys: ['kind', 'maxAbsoluteInput', 'maxAbsoluteInteger', 'supportedOperations'], maxAbsoluteInput: 1e9,
  },
  'ratio-proportion': {
    slug: 'oran-oranti-hesaplama', path: '/matematik/oran-oranti-hesaplama',
    sourceIds: ['openstax-ratio-proportion'], testVectorKind: 'ratio-proportion-oracle-v1',
    inputIds: ['mode', 'a', 'b', 'c'], resultIds: ['mode', 'a', 'b', 'c', 'x'],
    configKeys: ['kind', 'maxAbsoluteInput', 'supportedModes'], maxAbsoluteInput: 1e12,
  },
  pythagorean: {
    slug: 'pisagor-teoremi-hesaplama', path: '/matematik/pisagor-teoremi-hesaplama',
    sourceIds: ['openstax-pythagorean'], testVectorKind: 'pythagorean-oracle-v1',
    inputIds: ['mode', 'a', 'other'], resultIds: ['mode', 'a', 'b', 'c', 'unknown', 'result', 'perimeter', 'area'],
    configKeys: ['kind', 'maxAbsoluteInput', 'supportedModes'], maxAbsoluteInput: 1e12,
  },
};

interface UnitBrowserContract {
  readonly converterId: string;
  readonly slug: string;
  readonly path: string;
  readonly strategy: 'linear' | 'temperature';
  readonly allowNegative: boolean;
  readonly maxAbsoluteInput: number;
  readonly sourceIds: readonly string[];
  readonly unitIds: readonly string[];
}

/**
 * İmzalı yayın, aynı motor kimliğini taşıyan iki birim aracını birbirinin yerine
 * koyamasın diye tarayıcının anlayabildiği kimlik ve birim envanteri burada
 * kapalı tutulur. Katsayılar ayrıca pozitif/sonlu; fiziksel özel durumlar da
 * aşağıdaki semantik kapılardan geçer.
 */
const unitContracts: Readonly<Record<UnitToolId, UnitBrowserContract>> = {
  'unit-weight': {
    converterId: 'weight', slug: 'agirlik-hesaplama', path: '/birim-cevirici/agirlik-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['nist-si-conversion-factors'],
    unitIds: ['ton', 'kg', 'g', 'mg', 'mcg', 'lb', 'oz', 'karat', 'stone'],
  },
  'unit-area': {
    converterId: 'area', slug: 'alan-hesaplama', path: '/birim-cevirici/alan-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['nist-si-conversion-factors'],
    unitIds: ['km2', 'm2', 'cm2', 'mm2', 'hektar', 'donum', 'ft2', 'in2', 'yd2', 'akre'],
  },
  'unit-volume': {
    converterId: 'volume', slug: 'hacim-hesaplama', path: '/birim-cevirici/hacim-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['bipm-si-brochure', 'nist-si-conversion-factors'],
    unitIds: ['m3', 'L', 'mL', 'gal_us', 'gal_uk', 'ft3', 'in3', 'varil', 'bardak', 'yk', 'tk'],
  },
  'unit-speed': {
    converterId: 'speed', slug: 'hiz-hesaplama', path: '/birim-cevirici/hiz-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['nist-si-conversion-factors'],
    unitIds: ['km_sa', 'm_s', 'cm_s', 'mil_sa', 'knot', 'ft_s', 'mach', 'km_dk'],
  },
  'unit-temperature': {
    converterId: 'temperature', slug: 'sicaklik-hesaplama', path: '/birim-cevirici/sicaklik-hesaplama',
    strategy: 'temperature', allowNegative: true, maxAbsoluteInput: 1e9,
    sourceIds: ['bipm-si-brochure', 'nist-si-conversion-factors'], unitIds: ['C', 'F', 'K'],
  },
  'unit-length': {
    converterId: 'length', slug: 'uzunluk-hesaplama', path: '/birim-cevirici/uzunluk-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['bipm-si-brochure', 'nist-si-conversion-factors'],
    unitIds: ['km', 'm', 'cm', 'mm', 'mil', 'yard', 'ft', 'in', 'dm'],
  },
  'unit-data-size': {
    converterId: 'data-size', slug: 'veri-boyutu-hesaplama', path: '/birim-cevirici/veri-boyutu-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['nist-si-conversion-factors'],
    unitIds: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'bit', 'Kbit', 'Mbit', 'Gbit'],
  },
  'unit-time': {
    converterId: 'time', slug: 'zaman-hesaplama', path: '/birim-cevirici/zaman-hesaplama',
    strategy: 'linear', allowNegative: false, maxAbsoluteInput: 1e12,
    sourceIds: ['bipm-si-brochure'],
    unitIds: ['ms', 's', 'dk', 'sa', 'gun', 'hafta', 'ay', 'yil', 'on_yil', 'yuzyil'],
  },
};

const approvedUnitSources = {
  'nist-si-conversion-factors': {
    title: 'NIST Guide to the SI — Appendix B, Conversion Factors',
    publisher: 'National Institute of Standards and Technology',
    url: 'https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors',
    kind: 'official-government',
  },
  'bipm-si-brochure': {
    title: 'The International System of Units (SI Brochure)',
    publisher: 'Bureau International des Poids et Mesures',
    url: 'https://www.bipm.org/en/publications/si-brochure',
    kind: 'international-standards-body',
  },
} as const;

// Registry yalnız statik veri taşıyabilir; bu anahtarlar güvenilir imzayla
// gelse dahi tarayıcıya formül, ifade veya çalıştırılabilir kod sokamaz.
const forbiddenExecutableKeys = new Set([
  'code', 'eval', 'executable', 'expression', 'formula', 'javascript', 'script',
]);

type Digest = (value: string) => Promise<string>;
type SignatureVerifier = (
  canonicalManifest: string,
  signatureBase64: string,
  publicKeyBase64: string,
) => Promise<boolean>;

export interface VerifiedWebRegistrySnapshot {
  readonly releaseId: string;
  readonly activatedAt: string;
  readonly fetchedAt: string;
  readonly sequence: number;
  readonly manifest: ToolRegistryCurrentPointer;
  readonly tools: readonly ToolDefinition[];
}

export interface SignedRegistryFetchOptions {
  readonly endpoint: string;
  readonly pinnedKeyId: string;
  readonly pinnedPublicKeyBase64: string;
  readonly minimumSequenceExclusive?: number;
  readonly cachedSnapshot?: VerifiedWebRegistrySnapshot | null;
  readonly allowLocalhostHttp?: boolean;
  readonly fetchImplementation?: typeof fetch;
  readonly digestImplementation?: Digest;
  readonly verifySignatureImplementation?: SignatureVerifier;
  readonly now?: () => Date;
  readonly timeoutMs?: number;
}

export interface SignedRegistryBootstrapConfig {
  readonly endpoint: string;
  readonly keyId: string;
  readonly publicKeyBase64: string;
  readonly bundledSequence: number;
  readonly allowLocalhostHttp?: boolean;
}

export type RegistrySource = 'signed-remote' | 'signed-cache' | 'bundled-fallback';

export interface BrowserRegistryStatus {
  readonly source: RegistrySource;
  readonly message: string;
  readonly releaseId: string | null;
  readonly sequence: number | null;
  readonly tools: readonly ToolDefinition[];
}

interface CacheEnvelope {
  readonly cacheVersion: 1;
  readonly fetchedAt: string;
  readonly manifest: ToolRegistryCurrentPointer;
  readonly tools: readonly ToolDefinition[];
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !isArray(value);
}

function requiredString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Registry alanı geçersiz: ${key}.`);
  }
  return value;
}

function exactIsoTimestamp(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`Registry ${field} zamanı geçersiz.`);
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) {
    throw new Error(`Registry ${field} canonical UTC zamanı değil.`);
  }
  return value;
}

function isoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}

function parseVersion(value: string): readonly [number, number, number] | null {
  // Uzak eğitim profili sürümü imza kapsamındadır; başında sıfır bulunan
  // gevşek varyantları aynı semver gibi yorumlamayıp canonical biçimde kapatırız.
  const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.exec(value);
  if (!match) return null;
  const parts = match.slice(1).map(Number);
  return parts.every(Number.isSafeInteger)
    ? [parts[0], parts[1], parts[2]]
    : null;
}

function compareVersions(left: readonly [number, number, number], right: readonly [number, number, number]): -1 | 0 | 1 {
  for (let index = 0; index < 3; index += 1) {
    if (left[index] < right[index]) return -1;
    if (left[index] > right[index]) return 1;
  }
  return 0;
}

function versionSupported(required: string): boolean {
  const current = parseVersion(supportedWebClientVersion);
  const minimum = parseVersion(required);
  if (!current || !minimum) return false;
  for (let index = 0; index < 3; index += 1) {
    if (current[index] !== minimum[index]) return current[index] > minimum[index];
  }
  return true;
}

function isSupportedToolId(value: string): value is BrowserToolId {
  return toolIds.some(candidate => candidate === value);
}

function isUnitToolId(value: BrowserToolId): value is UnitToolId {
  return unitToolIds.some(candidate => candidate === value);
}

function isMathToolId(value: BrowserToolId): value is MathToolId {
  return mathToolIds.some(candidate => candidate === value);
}

function isHomeToolId(value: BrowserToolId): value is HomeToolId {
  return homeToolIds.some(candidate => candidate === value);
}

function isDateTimeToolId(value: BrowserToolId): value is DateTimeToolId {
  return dateTimeToolIds.some(candidate => candidate === value);
}

function isEducationToolId(value: BrowserToolId): value is EducationToolId {
  return educationToolIds.some(candidate => candidate === value);
}

function isEverydayHealthToolId(value: BrowserToolId): value is EverydayHealthToolId {
  return everydayHealthToolIds.some(candidate => candidate === value);
}

function isFinanceBusinessToolId(value: BrowserToolId): value is FinanceBusinessToolId {
  return financeBusinessToolIds.some(candidate => candidate === value);
}

function isSourcePreparedToolId(value: BrowserToolId): value is SourcePreparedToolId {
  return sourcePreparedToolIds.some(candidate => candidate === value);
}

function sameOrder(actual: readonly unknown[], expected: readonly unknown[]): boolean {
  return actual.length === expected.length
    && actual.every((item, index) => item === expected[index]);
}

function allowedBmrFormulaPath(
  root: unknown,
  path: readonly (string | number)[],
  formulaValue: unknown,
): boolean {
  const tools = arrayValue(root);
  if (tools === null || path.length < 6 || typeof path[0] !== 'number') return false;
  const tool = tools[path[0]];
  if (!isRecord(tool) || tool.toolId !== 'basal-metabolic-rate'
      || path[1] !== 'testVectors' || path[2] !== 'cases' || typeof path[3] !== 'number') return false;
  const testVectors = isRecord(tool.testVectors) ? tool.testVectors : null;
  const cases = testVectors ? arrayValue(testVectors.cases) : null;
  const testCase = cases?.[path[3]];
  if (!isRecord(testCase)) return false;
  const isInput = path.length === 6 && path[4] === 'input' && path[5] === 'formula';
  const isExpectedValue = path.length === 7
    && path[4] === 'expected' && path[5] === 'value' && path[6] === 'formula';
  if (!isInput && !isExpectedValue) return false;
  if (formulaValue === 'mifflin' || formulaValue === 'harris') return true;
  return isInput
    && formulaValue === 'owen'
    && testCase.caseId === 'unsupported-formula'
    && testCase.scenario === 'adversarial';
}

function registryDataSnapshot(value: unknown, depth = 0): unknown {
  if (depth > 40) throw new Error('Registry veri derinliği sınırı aşıldı.');
  if (value === null || typeof value !== 'object') return value;
  const array = isArray(value);
  const fields: Record<string, unknown> = {};
  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !('value' in descriptor)) throw new Error('Registry accessor alanı içeriyor.');
    if (typeof key !== 'string' || (!descriptor.enumerable && !(array && key === 'length'))) {
      throw new Error('Registry JSON dışı alan içeriyor.');
    }
    if (key === '__proto__' || key === 'prototype' || key === 'constructor') {
      throw new Error('Registry tehlikeli nesne anahtarı içeriyor.');
    }
    const child: unknown = descriptor.value;
    fields[key] = registryDataSnapshot(child, depth + 1);
  }
  if (!array) return Object.freeze(fields);
  const length = fields.length;
  if (typeof length !== 'number' || !Number.isSafeInteger(length) || length < 0 || Object.keys(fields).length !== length + 1) {
    throw new Error('Registry dizisi eksik veya fazladan alan içeriyor.');
  }
  const entries: unknown[] = [];
  for (let index = 0; index < length; index += 1) {
    if (!Object.hasOwn(fields, index)) throw new Error('Registry dizisi eksik hücre içeriyor.');
    entries.push(fields[String(index)]);
  }
  return Object.freeze(entries);
}

export function parseBrowserToolDefinitions(value: unknown): readonly ToolDefinition[] {
  const snapshot = registryDataSnapshot(value);
  assertBrowserToolDefinitions(snapshot);
  return snapshot;
}

function assertSafeJsonTree(
  value: unknown,
  depth = 0,
  path: readonly (string | number)[] = [],
  root: unknown = value,
): void {
  if (depth > 40) throw new Error('Registry veri derinliği sınırı aşıldı.');
  if (value === null || ['string', 'boolean'].includes(typeof value)) return;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Registry sonlu olmayan sayı içeriyor.');
    return;
  }
  const entries = arrayValue(value);
  if (entries !== null) {
    entries.forEach((child, index) => assertSafeJsonTree(child, depth + 1, [...path, index], root));
    return;
  }
  if (!isRecord(value)) throw new Error('Registry JSON dışı değer içeriyor.');
  for (const [key, child] of Object.entries(value)) {
    if (key === '__proto__' || key === 'prototype' || key === 'constructor') {
      throw new Error('Registry tehlikeli nesne anahtarı içeriyor.');
    }
    const childPath = [...path, key];
    if (forbiddenExecutableKeys.has(key.toLowerCase())
        && !(key.toLowerCase() === 'formula' && allowedBmrFormulaPath(root, childPath, child))) {
      throw new Error(`Registry çalıştırılabilir veri anahtarı içeriyor: ${key}.`);
    }
    assertSafeJsonTree(child, depth + 1, childPath, root);
  }
}

function numberIn(record: Record<string, unknown>, key: string, minimum: number, maximum: number): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
    throw new Error(`Registry sayısal alanı geçersiz: ${key}.`);
  }
  return value;
}

function assertPriceMap(value: unknown): void {
  if (!isRecord(value)) throw new Error('HGS sınıf tarifesi nesne değil.');
  for (let index = 1; index <= 6; index += 1) {
    const fee = value[`class-${index}`];
    if (fee !== null && (typeof fee !== 'number' || !Number.isFinite(fee) || fee < 0)) {
      throw new Error(`HGS class-${index} tarifesi geçersiz.`);
    }
  }
}

function assertExactKeys(
  record: Record<string, unknown>,
  expected: readonly string[],
  context: string,
): void {
  const actual = Object.keys(record).sort();
  const wanted = [...expected].sort();
  if (!sameOrder(actual, wanted)) {
    throw new Error(`${context} alan sözleşmesi geçersiz.`);
  }
}

function schemaFields(value: unknown, context: string): Record<string, unknown>[] {
  const fields = isRecord(value) && value.type === 'object' ? arrayValue(value.fields) : null;
  if (!isRecord(value) || fields === null) {
    throw new Error(`${context} nesne şeması geçersiz.`);
  }
  assertExactKeys(value, ['type', 'fields'], context);
  return fields.map((field) => {
    if (!isRecord(field)) throw new Error(`${context} alan kaydı geçersiz.`);
    return field;
  });
}

function isApprovedUnitSourceId(value: string): value is keyof typeof approvedUnitSources {
  return Object.hasOwn(approvedUnitSources, value);
}

function assertUnitSources(
  candidate: Record<string, unknown>,
  contract: UnitBrowserContract,
  toolId: UnitToolId,
): void {
  const sources = arrayValue(candidate.sources);
  if (sources === null) throw new Error(`${toolId} kaynak listesi geçersiz.`);
  const sourceIds: string[] = [];
  for (const source of sources) {
    if (!isRecord(source)) throw new Error(`${toolId} kaynak kaydı geçersiz.`);
    assertExactKeys(source, [
      'sourceId', 'sourceGroupId', 'title', 'publisher', 'url', 'kind', 'reviewedOn', 'watchFields',
    ], `${toolId} kaynak`);
    const sourceId = requiredString(source, 'sourceId');
    if (!isApprovedUnitSourceId(sourceId)) throw new Error(`${toolId} onaysız kaynak içeriyor.`);
    const approved = approvedUnitSources[sourceId];
    const watchFields = arrayValue(source.watchFields);
    if (
      source.sourceGroupId !== null
      || source.title !== approved.title
      || source.publisher !== approved.publisher
      || source.url !== approved.url
      || source.kind !== approved.kind
      || !isoDate(source.reviewedOn)
      || watchFields === null
      || watchFields.length === 0
      || watchFields.some((field) => typeof field !== 'string' || field.trim() === '')
    ) {
      throw new Error(`${toolId} kaynak metadata sözleşmesi geçersiz.`);
    }
    sourceIds.push(sourceId);
  }
  if (!sameOrder(sourceIds, contract.sourceIds)) {
    throw new Error(`${toolId} kaynak kimlikleri veya sırası geçersiz.`);
  }
}

function assertUnitInputSchema(
  value: unknown,
  units: readonly Record<string, unknown>[],
  contract: UnitBrowserContract,
  toolId: UnitToolId,
): void {
  const fields = schemaFields(value, `${toolId} inputSchema`);
  const expectedIds = ['value', 'fromUnit', 'toUnit'];
  if (!sameOrder(fields.map((field) => String(field.id)), expectedIds)) {
    throw new Error(`${toolId} girdi alanları eksik veya sırası geçersiz.`);
  }
  const fieldKeys = [
    'id', 'type', 'label', 'required', 'defaultValue', 'placeholder', 'unit',
    'minimum', 'maximum', 'maxDecimalPlaces', 'options', 'visibleWhen',
  ];
  fields.forEach((field) => assertExactKeys(field, fieldKeys, `${toolId} girdi alanı`));

  const valueField = fields[0];
  const valueOptions = arrayValue(valueField.options);
  const expectedMinimum = contract.allowNegative ? -contract.maxAbsoluteInput : 0;
  if (
    valueField.type !== 'number'
    || valueField.label !== 'Değer'
    || valueField.required !== true
    || valueField.defaultValue !== null
    || valueField.placeholder !== 'Çevrilecek değeri girin'
    || valueField.unit !== null
    || valueField.minimum !== expectedMinimum
    || valueField.maximum !== contract.maxAbsoluteInput
    || valueField.maxDecimalPlaces !== 8
    || valueOptions === null
    || valueOptions.length !== 0
    || valueField.visibleWhen !== null
  ) throw new Error(`${toolId} değer girdi descriptor'ı geçersiz.`);

  const optionValues = units.map((unit) => requiredString(unit, 'id'));
  const optionLabels = units.map((unit) =>
    `${requiredString(unit, 'label')} (${requiredString(unit, 'symbol')})`);
  for (const [index, expectedLabel] of ['Kaynak birim', 'Hedef birim'].entries()) {
    const field = fields[index + 1];
    const options = arrayValue(field.options);
    if (
      field.type !== 'select'
      || field.label !== expectedLabel
      || field.required !== true
      || field.defaultValue !== null
      || field.placeholder !== null
      || field.unit !== null
      || field.minimum !== null
      || field.maximum !== null
      || field.maxDecimalPlaces !== null
      || field.visibleWhen !== null
      || options === null
      || options.length !== units.length
    ) throw new Error(`${toolId} birim seçim descriptor'ı geçersiz.`);
    const actualValues: string[] = [];
    const actualLabels: string[] = [];
    for (const option of options) {
      if (!isRecord(option)) throw new Error(`${toolId} birim seçeneği geçersiz.`);
      assertExactKeys(option, ['value', 'label'], `${toolId} birim seçeneği`);
      actualValues.push(requiredString(option, 'value'));
      actualLabels.push(requiredString(option, 'label'));
    }
    if (!sameOrder(actualValues, optionValues) || !sameOrder(actualLabels, optionLabels)) {
      throw new Error(`${toolId} birim seçenekleri config ile birebir eşleşmiyor.`);
    }
  }
}

function assertUnitResultSchema(value: unknown, toolId: UnitToolId): void {
  const fields = schemaFields(value, `${toolId} resultSchema`);
  const expected = [
    { id: 'converterId', type: 'text', label: 'Dönüştürücü', prominent: false },
    { id: 'input', type: 'number', label: 'Girdi', prominent: false },
    { id: 'fromUnit', type: 'text', label: 'Kaynak birim', prominent: false },
    { id: 'toUnit', type: 'text', label: 'Hedef birim', prominent: false },
    { id: 'result', type: 'number', label: 'Sonuç', prominent: true },
    { id: 'conversions', type: 'number-map', label: 'Tüm dönüşümler', prominent: false },
  ] as const;
  if (fields.length !== expected.length) throw new Error(`${toolId} sonuç alanları eksik.`);
  fields.forEach((field, index) => {
    assertExactKeys(
      field,
      ['id', 'type', 'label', 'unit', 'nullable', 'prominent'],
      `${toolId} sonuç alanı`,
    );
    const descriptor = expected[index];
    if (
      field.id !== descriptor.id
      || field.type !== descriptor.type
      || field.label !== descriptor.label
      || field.unit !== null
      || field.nullable !== false
      || field.prominent !== descriptor.prominent
    ) throw new Error(`${toolId} sonuç descriptor'ı geçersiz.`);
  });
}

function assertUnitToolDefinition(
  candidate: Record<string, unknown>,
  toolId: UnitToolId,
): void {
  const contract = unitContracts[toolId];
  const config = candidate.config;
  if (!isRecord(config) || !isRecord(candidate.category)) {
    throw new Error(`${toolId} config veya kategori kaydı geçersiz.`);
  }
  if (
    candidate.slug !== contract.slug
    || candidate.path !== contract.path
    || candidate.classification !== 'timeless'
    || candidate.category.id !== 'birim-cevirici'
    || candidate.category.label !== 'Birim Çevirici'
  ) throw new Error(`${toolId} kimlik/rota/kategori sözleşmesi geçersiz.`);
  assertExactKeys(config, [
    'kind', 'converterId', 'strategy', 'allowNegative', 'maxAbsoluteInput', 'units',
  ], `${toolId} config`);
  const configuredUnits = arrayValue(config.units);
  if (
    config.converterId !== contract.converterId
    || config.strategy !== contract.strategy
    || config.allowNegative !== contract.allowNegative
    || config.maxAbsoluteInput !== contract.maxAbsoluteInput
    || configuredUnits === null
  ) throw new Error(`${toolId} dönüştürücü config sözleşmesi geçersiz.`);

  const units: Record<string, unknown>[] = [];
  const seenIds = new Set<string>();
  const seenSymbols = new Set<string>();
  for (const unit of configuredUnits) {
    if (!isRecord(unit)) throw new Error(`${toolId} birim kaydı geçersiz.`);
    assertExactKeys(unit, ['id', 'label', 'symbol', 'factor'], `${toolId} birim`);
    const id = requiredString(unit, 'id');
    const symbol = requiredString(unit, 'symbol');
    requiredString(unit, 'label');
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(id) || seenIds.has(id) || seenSymbols.has(symbol)) {
      throw new Error(`${toolId} birim kimliği veya simgesi tekrarlı/geçersiz.`);
    }
    if (typeof unit.factor !== 'number' || !Number.isFinite(unit.factor) || unit.factor <= 0) {
      throw new Error(`${toolId} birim katsayısı pozitif ve sonlu olmalıdır.`);
    }
    seenIds.add(id);
    seenSymbols.add(symbol);
    units.push(unit);
  }
  if (!sameOrder(units.map((unit) => requiredString(unit, 'id')), contract.unitIds)) {
    throw new Error(`${toolId} birim envanteri eksik veya sırası geçersiz.`);
  }
  if (contract.strategy === 'temperature' && units.some((unit) => unit.factor !== 1)) {
    throw new Error('Sıcaklık C/F/K ölçek katsayıları 1 olmalıdır.');
  }
  if (toolId === 'unit-time') {
    const assumptions = new Map<string, number>([
      ['ay', 2_592_000], ['yil', 31_557_600],
      ['on_yil', 315_576_000], ['yuzyil', 3_155_760_000],
    ]);
    for (const [id, expectedFactor] of assumptions) {
      if (units.find((unit) => unit.id === id)?.factor !== expectedFactor) {
        throw new Error(`Zaman varsayımı ${id} için ${expectedFactor} saniye olmalıdır.`);
      }
    }
  }

  assertUnitSources(candidate, contract, toolId);
  assertUnitInputSchema(candidate.inputSchema, units, contract, toolId);
  assertUnitResultSchema(candidate.resultSchema, toolId);
  if (!isRecord(candidate.testVectors) || candidate.testVectors.kind !== 'unit-converter-oracle-v1') {
    throw new Error(`${toolId} oracle sözleşmesi geçersiz.`);
  }
}

function assertExactStringList(
  value: unknown,
  expected: readonly string[],
  context: string,
): void {
  const entries = arrayValue(value);
  if (entries === null
      || entries.some((item) => typeof item !== 'string')
      || !sameOrder(entries, expected)) {
    throw new Error(`${context} kapalı seçenek sözleşmesi geçersiz.`);
  }
}

function assertMathToolDefinition(
  candidate: Record<string, unknown>,
  toolId: MathToolId,
): void {
  const contract = mathContracts[toolId];
  const config = candidate.config;
  if (!isRecord(config) || !isRecord(candidate.category)) {
    throw new Error(`${toolId} config veya kategori kaydı geçersiz.`);
  }
  if (
    candidate.slug !== contract.slug
    || candidate.path !== contract.path
    || candidate.classification !== 'timeless'
    || candidate.category.id !== 'matematik'
    || candidate.category.label !== 'Matematik'
  ) throw new Error(`${toolId} kimlik/rota/kategori sözleşmesi geçersiz.`);

  assertExactKeys(config, contract.configKeys, `${toolId} config`);
  if (config.maxAbsoluteInput !== contract.maxAbsoluteInput) {
    throw new Error(`${toolId} azami girdi sınırı sözleşmesi geçersiz.`);
  }

  switch (toolId) {
    case 'geometry':
      assertExactStringList(config.supportedShapes, [
        'square', 'rectangle', 'triangle', 'circle', 'trapezoid',
        'parallelogram', 'cube', 'sphere', 'cylinder', 'cone',
      ], `${toolId} şekilleri`);
      break;
    case 'prime-number':
      if (config.maxInteger !== '1000000000000000') {
        throw new Error(`${toolId} tamsayı sınırı sözleşmesi geçersiz.`);
      }
      break;
    case 'gcd-lcm':
      if (config.maxInteger !== '1000000000000' || config.minValues !== 2 || config.maxValues !== 6) {
        throw new Error(`${toolId} tamsayı/adet sınırı sözleşmesi geçersiz.`);
      }
      break;
    case 'power-root':
      if (config.maxN !== 170) throw new Error(`${toolId} n sınırı sözleşmesi geçersiz.`);
      assertExactStringList(config.supportedModes, [
        'power', 'sqrt', 'nth-root', 'logarithm', 'factorial', 'absolute',
      ], `${toolId} modları`);
      break;
    case 'quadratic-equation':
      break;
    case 'fraction':
      if (config.maxAbsoluteInteger !== 1e9) {
        throw new Error(`${toolId} tamsayı sınırı sözleşmesi geçersiz.`);
      }
      assertExactStringList(config.supportedOperations, [
        'add', 'subtract', 'multiply', 'divide',
      ], `${toolId} işlemleri`);
      break;
    case 'ratio-proportion':
      assertExactStringList(config.supportedModes, ['direct', 'inverse'], `${toolId} modları`);
      break;
    case 'pythagorean':
      assertExactStringList(config.supportedModes, ['hypotenuse', 'leg'], `${toolId} modları`);
      break;
  }

  const inputFields = schemaFields(candidate.inputSchema, `${toolId} inputSchema`);
  const resultFields = schemaFields(candidate.resultSchema, `${toolId} resultSchema`);
  if (!sameOrder(inputFields.map((field) => String(field.id)), contract.inputIds)
      || !sameOrder(resultFields.map((field) => String(field.id)), contract.resultIds)) {
    throw new Error(`${toolId} girdi/sonuç alan sözleşmesi geçersiz.`);
  }

  const sources = arrayValue(candidate.sources);
  const sourceIds = sources !== null
    ? sources.map((source) => isRecord(source) ? source.sourceId : undefined)
    : [];
  if (!sameOrder(sourceIds, contract.sourceIds)) {
    throw new Error(`${toolId} kaynak kimliği sözleşmesi geçersiz.`);
  }
  const cases = isRecord(candidate.testVectors) ? arrayValue(candidate.testVectors.cases) : null;
  if (!isRecord(candidate.testVectors)
      || candidate.testVectors.kind !== contract.testVectorKind
      || cases === null
      || cases.length === 0) {
    throw new Error(`${toolId} oracle sözleşmesi geçersiz.`);
  }
}

function assertHomeToolDefinition(
  candidate: Record<string, unknown>,
  toolId: HomeToolId,
): void {
  const cases = isRecord(candidate.testVectors) ? arrayValue(candidate.testVectors.cases) : null;
  const bundled = getBrowserBundledPilotTools().find((tool) => tool.toolId === toolId);
  const config = candidate.config;
  if (!bundled || !isRecord(config) || !isRecord(candidate.category)) {
    throw new Error(`${toolId} ev/tadilat sözleşmesi bulunamadı.`);
  }
  if (
    candidate.slug !== bundled.slug
    || candidate.path !== bundled.path
    || candidate.classification !== 'timeless'
    || candidate.category.id !== 'ev-tadilat'
    || candidate.category.label !== 'Ev ve Tadilat'
  ) throw new Error(`${toolId} kimlik/rota/kategori sözleşmesi geçersiz.`);

  if (toolId === 'curtain-fabric') {
    const inputFields = schemaFields(candidate.inputSchema, `${toolId} inputSchema`);
    const resultFields = schemaFields(candidate.resultSchema, `${toolId} resultSchema`);
    if (inputFields.some((field) => field.id === 'toolId')
        || !resultFields.some((field) => field.id === 'toolId')
        || !isRecord(candidate.testVectors)
        || candidate.testVectors.kind !== 'curtain-fabric-oracle-v2'
        || cases === null
        || cases.length < 11) {
      throw new Error(`${toolId} v2 girdi/sonuç/oracle sözleşmesi geçersiz.`);
    }
    // Tül ve fon modları tek kapalı v2 descriptor'da birleştiği için tüm
    // yürütülebilir semantik alanlar paketli Exact54 baseline'ıyla byte-anlamsal
    // eşleşir; imza tek başına yeni mod veya formül açamaz.
    for (const field of ['config', 'inputSchema', 'resultSchema', 'testVectors', 'sources'] as const) {
      if (canonicalRegistryJson(candidate[field]) !== canonicalRegistryJson(bundled[field])) {
        throw new Error(`${toolId} ${field} kapalı baseline ile eşleşmiyor.`);
      }
    }
    return;
  }

  assertExactKeys(
    config,
    ['kind', 'toolId', 'maxAbsoluteInput', 'metadata'],
    `${toolId} config`,
  );
  if (
    config.kind !== 'home-renovation-v1'
    || config.toolId !== toolId
    || config.maxAbsoluteInput !== 1e9
    || !isRecord(config.metadata)
    || config.metadata.calculatorId !== toolId
  ) throw new Error(`${toolId} ev/tadilat config sözleşmesi geçersiz.`);

  const inputFields = schemaFields(candidate.inputSchema, `${toolId} inputSchema`);
  if (inputFields.some((field) => field.id === 'toolId')) {
    throw new Error(`${toolId} iç araç kimliğini kullanıcı form alanı yapamaz.`);
  }
  const resultFields = schemaFields(candidate.resultSchema, `${toolId} resultSchema`);
  if (!resultFields.some((field) => field.id === 'toolId')) {
    throw new Error(`${toolId} sonuç discriminantı eksik.`);
  }
  if (!isRecord(candidate.testVectors)
      || candidate.testVectors.kind !== 'home-renovation-oracle-v1'
      || cases === null
      || cases.length < 6) {
    throw new Error(`${toolId} bağımsız oracle sözleşmesi geçersiz.`);
  }

  // İmzalı yayın dahi config, descriptor, kaynak veya oracle'yı birlikte
  // tahrif ederek web motorunu farklı bir sözleşmeye geçiremez. Yeni yöntem
  // ancak yeni web sürümü ve yeni kapalı baseline ile kabul edilir.
  for (const field of ['config', 'inputSchema', 'resultSchema', 'testVectors', 'sources'] as const) {
    if (canonicalRegistryJson(candidate[field]) !== canonicalRegistryJson(bundled[field])) {
      throw new Error(`${toolId} ${field} kapalı baseline ile eşleşmiyor.`);
    }
  }
}

const sourceKinds = new Set([
  'official-government',
  'official-operator',
  'authoritative-education',
  'peer-reviewed-medical-research',
  'international-standards-body',
]);

const registryOracleScenarios = new Set([
  'positive', 'zero', 'boundary', 'leap-day', 'month-end', 'dst-calendar',
  'year-boundary', 'threshold', 'weighted', 'unweighted', 'adversarial',
]);

const registryOracleErrorCodes = new Set([
  'REQUIRED', 'NOT_FINITE', 'OUT_OF_RANGE', 'DIVISION_BY_ZERO',
  'UNSUPPORTED_VALUE', 'INVALID_CONFIG', 'CALCULATION_OVERFLOW', 'NO_CONVERGENCE',
]);

function bundledToolDefinition(
  toolId: BrowserToolId,
): ToolDefinition {
  const bundled = getBrowserBundledPilotTools().find((tool) => tool.toolId === toolId);
  if (!bundled) throw new Error(`${toolId} paketli sözleşmesi bulunamadı.`);
  return bundled;
}

function assertCanonicalField(
  candidate: Record<string, unknown>,
  bundled: ToolDefinition,
  field: 'config' | 'inputSchema' | 'resultSchema' | 'testVectors' | 'sources' | 'updateCadence' | 'content',
  toolId: DateTimeToolId | EducationToolId | EverydayHealthToolId | FinanceBusinessToolId,
): void {
  if (canonicalRegistryJson(candidate[field]) !== canonicalRegistryJson(bundled[field])) {
    throw new Error(`${toolId} ${field} kapalı baseline ile eşleşmiyor.`);
  }
}

const financeForwardConfigToolIds = new Set<FinanceBusinessToolId>([
  'consumer-loan', 'depreciation-plan', 'pos-commission',
]);
const financeMutableProjectionValue = '<signed-policy-renewal>';
const financeLifecycleKeys = [
  'configVersion', 'effectiveFrom', 'effectiveTo', 'reviewAfter', 'reviewedOn', 'sourceIds',
] as const;

function financeLifecycleImmutableProjection(value: unknown): unknown {
  if (!isRecord(value)) return value;
  // Spread bilinmeyen anahtarları korur; böylece projection karşılaştırması
  // allowlist dışındaki ek alanları sessizce düşürmez.
  return {
    ...value,
    configVersion: financeMutableProjectionValue,
    effectiveFrom: financeMutableProjectionValue,
    effectiveTo: financeMutableProjectionValue,
    reviewAfter: financeMutableProjectionValue,
    reviewedOn: financeMutableProjectionValue,
  };
}

function financeConfigImmutableProjection(value: unknown): unknown {
  if (!isRecord(value)) return value;
  const projected = {
    ...value,
    policyLifecycle: financeLifecycleImmutableProjection(value.policyLifecycle),
  };
  if (!isRecord(value.policy)) return projected;
  if (value.kind === 'consumer-loan-v1') {
    return {
      ...projected,
      policy: {
        ...value.policy,
        bsmvRate: financeMutableProjectionValue,
        kkdfRate: financeMutableProjectionValue,
        maximumTermTiers: financeMutableProjectionValue,
      },
    };
  }
  if (value.kind === 'depreciation-plan-v1') {
    return {
      ...projected,
      policy: {
        ...value.policy,
        decliningBalanceMultiplier: financeMutableProjectionValue,
        maximumDecliningRate: financeMutableProjectionValue,
        policyLifecycle: financeLifecycleImmutableProjection(value.policy.policyLifecycle),
      },
    };
  }
  if (value.kind === 'pos-commission-v1') {
    return {
      ...projected,
      policy: {
        ...value.policy,
        bsmvRate: financeMutableProjectionValue,
        policyLifecycle: financeLifecycleImmutableProjection(value.policy.policyLifecycle),
      },
    };
  }
  return projected;
}

function financeToolImmutableProjection(value: unknown): unknown {
  if (!isRecord(value)) throw new Error('Finans araç zarfı geçersiz.');
  const cadence = isRecord(value.updateCadence)
    ? { ...value.updateCadence, reviewAfter: financeMutableProjectionValue }
    : value.updateCadence;
  const sourceEntries = arrayValue(value.sources);
  const sources = sourceEntries !== null
    ? sourceEntries.map((source) => isRecord(source)
      ? { ...source, reviewedOn: financeMutableProjectionValue }
      : source)
    : value.sources;
  return {
    ...value,
    configVersion: financeMutableProjectionValue,
    effectiveFrom: financeMutableProjectionValue,
    effectiveTo: financeMutableProjectionValue,
    lastReviewed: financeMutableProjectionValue,
    updateCadence: cadence,
    sources,
    config: financeConfigImmutableProjection(value.config),
  };
}

function assertFinanceLifecycle(
  value: unknown,
  expectedValue: unknown,
  candidate: Record<string, unknown>,
  context: string,
  bindWholeTool: boolean,
): void {
  if (!isRecord(value) || !isRecord(expectedValue)) {
    throw new Error(`${context} policy lifecycle kaydı geçersiz.`);
  }
  assertExactKeys(value, financeLifecycleKeys, `${context} policy lifecycle`);
  const configVersion = requiredString(value, 'configVersion');
  const effectiveFrom = value.effectiveFrom;
  const effectiveTo = value.effectiveTo;
  const reviewedOn = value.reviewedOn;
  const reviewAfter = value.reviewAfter;
  const sourceIds = arrayValue(value.sourceIds);
  const expectedSourceIds = arrayValue(expectedValue.sourceIds);
  if (
    !isoDate(effectiveFrom)
    || (effectiveTo !== null && !isoDate(effectiveTo))
    || !isoDate(reviewedOn)
    || (reviewAfter !== null && !isoDate(reviewAfter))
    || sourceIds === null
    || sourceIds.some((sourceId) => typeof sourceId !== 'string')
    || expectedSourceIds === null
    || !sameOrder(sourceIds, expectedSourceIds)
    || configVersion !== candidate.configVersion
    || effectiveFrom !== candidate.effectiveFrom
    || reviewedOn !== candidate.lastReviewed
    || reviewedOn < effectiveFrom
    || (effectiveTo !== null && effectiveTo < effectiveFrom)
    || (reviewAfter !== null && reviewAfter <= reviewedOn)
    || (candidate.toolId !== 'consumer-loan' && candidate.toolId !== 'pos-commission' && reviewAfter !== null && effectiveTo !== null && reviewAfter <= effectiveTo)
  ) throw new Error(`${context} policy lifecycle sürüm, tarih veya sourceId bağı geçersiz.`);

  if (!isRecord(candidate.updateCadence)) {
    throw new Error(`${context} araç inceleme periyodu geçersiz.`);
  }
  if (bindWholeTool && (
    effectiveTo !== candidate.effectiveTo
    || reviewAfter !== candidate.updateCadence.reviewAfter
  )) throw new Error(`${context} policy lifecycle üst araç tarihleriyle eşleşmiyor.`);

  const sources = arrayValue(candidate.sources) ?? [];
  for (const sourceId of sourceIds) {
    const source = sources.find((item) => isRecord(item) && item.sourceId === sourceId);
    if (!isRecord(source) || source.reviewedOn !== reviewedOn) {
      throw new Error(`${context} policy lifecycle kaynak inceleme tarihiyle eşleşmiyor.`);
    }
  }
}

function assertFinanceForwardPolicy(
  candidate: Record<string, unknown>,
  bundled: ToolDefinition,
  toolId: FinanceBusinessToolId,
): void {
  const config = candidate.config;
  const bundledConfig = bundled.config;
  if (!isRecord(config) || !isRecord(bundledConfig) || !isRecord(candidate.updateCadence)) {
    throw new Error(`${toolId} forward-policy kaydı geçersiz.`);
  }
  assertExactKeys(candidate.updateCadence, [
    'mode', 'days', 'reviewAfter', 'specialMonths', 'note',
  ], `${toolId} inceleme periyodu`);
  const reviewAfter = candidate.updateCadence.reviewAfter;
  const lastReviewed = requiredString(candidate, 'lastReviewed');
  if (
    !isoDate(lastReviewed)
    ||
    (reviewAfter !== null && !isoDate(reviewAfter))
    || (reviewAfter !== null && reviewAfter <= lastReviewed)
  ) throw new Error(`${toolId} inceleme tarihi geçersiz.`);

  if (!('policyLifecycle' in bundledConfig) || !('policy' in bundledConfig)) {
    throw new Error(`${toolId} forward-policy paket sözleşmesi geçersiz.`);
  }
  assertFinanceLifecycle(
    config.policyLifecycle,
    bundledConfig.policyLifecycle,
    candidate,
    `${toolId} tool`,
    true,
  );

  if (!isRecord(config.policy) || !isRecord(bundledConfig.policy)) {
    throw new Error(`${toolId} policy kaydı geçersiz.`);
  }
  assertExactKeys(config.policy, Object.keys(bundledConfig.policy), `${toolId} policy`);

  if (toolId === 'consumer-loan') {
    numberIn(config.policy, 'bsmvRate', 0, 1);
    numberIn(config.policy, 'kkdfRate', 0, 1);
    const tiers = arrayValue(config.policy.maximumTermTiers);
    if (tiers === null || tiers.length === 0) {
      throw new Error('consumer-loan vade bantları eksik.');
    }
    let previousThreshold = 0;
    for (const [index, tier] of tiers.entries()) {
      if (!isRecord(tier)) throw new Error('consumer-loan vade bandı geçersiz.');
      assertExactKeys(tier, ['upToPrincipal', 'maximumMonths'], 'consumer-loan vade bandı');
      const isLast = index === tiers.length - 1;
      const threshold = tier.upToPrincipal;
      if (
        (isLast
          ? threshold !== null
          : typeof threshold !== 'number' || !Number.isFinite(threshold) || threshold <= previousThreshold)
        || typeof tier.maximumMonths !== 'number'
        || !Number.isSafeInteger(tier.maximumMonths)
        || tier.maximumMonths < 1
      ) throw new Error('consumer-loan vade bantları artan eşik ve son null sözleşmesine uymuyor.');
      if (typeof threshold === 'number') previousThreshold = threshold;
    }
  } else if (toolId === 'depreciation-plan') {
    numberIn(config.policy, 'decliningBalanceMultiplier', 1, 10);
    numberIn(config.policy, 'maximumDecliningRate', Number.MIN_VALUE, 1);
    assertFinanceLifecycle(
      config.policy.policyLifecycle,
      'policyLifecycle' in bundledConfig.policy ? bundledConfig.policy.policyLifecycle : undefined,
      candidate,
      `${toolId} declining-balance`,
      false,
    );
    const nested = config.policy.policyLifecycle;
    if (!isRecord(nested) || nested.effectiveTo === null || nested.reviewAfter === null) {
      throw new Error('depreciation-plan tarihli nested policy gerektirir.');
    }
  } else if (toolId === 'pos-commission') {
    numberIn(config.policy, 'bsmvRate', 0, 1);
    assertFinanceLifecycle(
      config.policy.policyLifecycle,
      'policyLifecycle' in bundledConfig.policy ? bundledConfig.policy.policyLifecycle : undefined,
      candidate,
      `${toolId} commission-plus-bsmv`,
      false,
    );
    const nested = config.policy.policyLifecycle;
    if (!isRecord(nested) || nested.reviewAfter === null) {
      throw new Error('pos-commission inceleme tarihli nested policy gerektirir.');
    }
  }
}

function assertFinanceBusinessToolDefinition(
  candidate: Record<string, unknown>,
  toolId: FinanceBusinessToolId,
): void {
  const bundled = bundledToolDefinition(toolId);
  const currentVersion = parseVersion(requiredString(candidate, 'configVersion'));
  const baselineVersion = parseVersion(bundled.configVersion);
  const versionOrder = currentVersion === null || baselineVersion === null ? null : compareVersions(currentVersion, baselineVersion);
  const supportsForwardPolicy = financeForwardConfigToolIds.has(toolId);
  if (versionOrder === null || versionOrder < 0 || (!supportsForwardPolicy && versionOrder !== 0)) {
    throw new Error(
      supportsForwardPolicy
        ? `${toolId} configVersion strict semver ve baseline ${bundled.configVersion} veya yeni olmalıdır.`
        : `${toolId} saf finans sözleşmesi exact ${bundled.configVersion} configVersion gerektirir.`,
    );
  }

  // Baseline tam ToolDefinition'dır. Aynı sürümde tarih/policy oynatmak yeni
  // sürüm beyan etmeden eski onayı tekrar kullanmak olacağı için reddedilir.
  if (versionOrder === 0) {
    if (canonicalRegistryJson(candidate) !== canonicalRegistryJson(bundled)) {
      throw new Error(`${toolId} baseline ToolDefinition kaydı paketli onayla birebir eşleşmiyor.`);
    }
    return;
  }

  if (
    canonicalRegistryJson(financeToolImmutableProjection(candidate))
    !== canonicalRegistryJson(financeToolImmutableProjection(bundled))
  ) {
    throw new Error(`${toolId} ileri sürümü immutable ekran/içerik/kaynak/config yapısını değiştiremez.`);
  }
  assertFinanceForwardPolicy(candidate, bundled, toolId);
}

function assertDateTimeToolDefinition(
  candidate: Record<string, unknown>,
  toolId: DateTimeToolId,
): void {
  const bundled = bundledToolDefinition(toolId);
  if (!isRecord(candidate.category)
      || candidate.slug !== bundled.slug
      || candidate.path !== bundled.path
      || candidate.classification !== 'timeless'
      || candidate.configVersion !== bundled.configVersion
      || candidate.category.id !== 'tarih-zaman'
      || candidate.category.label !== 'Tarih ve Zaman') {
    throw new Error(`${toolId} kimlik/rota/kategori sözleşmesi geçersiz.`);
  }
  for (const field of [
    'config', 'inputSchema', 'resultSchema', 'testVectors', 'sources', 'updateCadence',
  ] as const) assertCanonicalField(candidate, bundled, field, toolId);
}

function assertRemoteSources(candidate: Record<string, unknown>, toolId: EducationToolId): void {
  const sources = arrayValue(candidate.sources);
  if (sources === null || sources.length === 0) {
    throw new Error(`${toolId} kaynak listesi geçersiz.`);
  }
  const sourceIds = new Set<string>();
  let hasOfficialGovernmentSource = false;
  for (const source of sources) {
    if (!isRecord(source)) throw new Error(`${toolId} kaynak kaydı geçersiz.`);
    assertExactKeys(source, [
      'sourceId', 'sourceGroupId', 'title', 'publisher', 'url', 'kind', 'reviewedOn', 'watchFields',
    ], `${toolId} kaynak`);
    const sourceId = requiredString(source, 'sourceId');
    if (!/^[a-z0-9][a-z0-9-]{1,127}$/.test(sourceId) || sourceIds.has(sourceId)) {
      throw new Error(`${toolId} kaynak kimliği tekrarlı veya geçersiz.`);
    }
    sourceIds.add(sourceId);
    if (source.sourceGroupId !== null
        && (typeof source.sourceGroupId !== 'string' || source.sourceGroupId.trim() === '')) {
      throw new Error(`${toolId} kaynak grup kimliği geçersiz.`);
    }
    requiredString(source, 'title');
    requiredString(source, 'publisher');
    const watchFields = arrayValue(source.watchFields);
    if (!requiredString(source, 'url').startsWith('https://')
        || typeof source.kind !== 'string'
        || !sourceKinds.has(source.kind)
        || !isoDate(source.reviewedOn)
        || watchFields === null
        || watchFields.length === 0
        || watchFields.some((field) => typeof field !== 'string' || field.trim() === '')) {
      throw new Error(`${toolId} kaynak metadata sözleşmesi geçersiz.`);
    }
    if (source.reviewedOn > String(candidate.lastReviewed)) {
      throw new Error(`${toolId} kaynak inceleme tarihi araç incelemesinden ileride.`);
    }
    if (source.kind === 'official-government') hasOfficialGovernmentSource = true;
  }
  if (!hasOfficialGovernmentSource) {
    throw new Error(`${toolId} kurum profili resmî kamu kaynağı içermiyor.`);
  }
}

function assertRemoteLifecycle(candidate: Record<string, unknown>, toolId: EducationToolId): void {
  const version = parseVersion(requiredString(candidate, 'configVersion'));
  const baseline = parseVersion('1.0.0');
  if (!version
      || !baseline
      || compareVersions(version, baseline) < 0
      || !isoDate(candidate.effectiveFrom)
      || !isoDate(candidate.lastReviewed)) {
    throw new Error(`${toolId} sürüm/yürürlük/inceleme kaydı geçersiz.`);
  }
  if (candidate.lastReviewed < candidate.effectiveFrom) {
    throw new Error(`${toolId} son inceleme tarihi yürürlük tarihinden önce.`);
  }
  const cadence = candidate.updateCadence;
  if (!isRecord(cadence)) throw new Error(`${toolId} inceleme periyodu eksik.`);
  assertExactKeys(cadence, [
    'mode', 'days', 'reviewAfter', 'specialMonths', 'note',
  ], `${toolId} inceleme periyodu`);
  const specialMonths = arrayValue(cadence.specialMonths);
  if (cadence.mode !== 'scheduled-source-review'
      || typeof cadence.days !== 'number'
      || !Number.isSafeInteger(cadence.days)
      || cadence.days < 1
      || cadence.days > 3650
      || !isoDate(cadence.reviewAfter)
      || cadence.reviewAfter <= candidate.lastReviewed
      || specialMonths === null
      || specialMonths.some((month) => typeof month !== 'number' || !Number.isSafeInteger(month) || month < 1 || month > 12)
      || new Set(specialMonths).size !== specialMonths.length
      || typeof cadence.note !== 'string'
      || cadence.note.trim() === '') {
    throw new Error(`${toolId} planlı kaynak inceleme sözleşmesi geçersiz.`);
  }
}

type GradeScenarioMode = 'relative' | 'weighted' | 'unweighted';

function assertGradeScenario(value: unknown, mode: GradeScenarioMode, context: string): void {
  if (!isRecord(value)) throw new Error(`${context} senaryosu geçersiz.`);
  assertExactKeys(value, ['id', 'label', 'kind', 'bands'], `${context} senaryosu`);
  const bands = arrayValue(value.bands);
  const scenarioId = requiredString(value, 'id');
  if (!/^[a-z0-9][a-z0-9-]{1,127}$/.test(scenarioId)
      || !['example', 'institution'].includes(String(value.kind))
      || bands === null
      || bands.length < 2
      || bands.length > 32) {
    throw new Error(`${context} senaryo kimliği/türü/bant sayısı geçersiz.`);
  }
  requiredString(value, 'label');
  const labels = new Set<string>();
  let previousMinimum = Number.POSITIVE_INFINITY;
  bands.forEach((band, index) => {
    if (!isRecord(band)) throw new Error(`${context} bant kaydı geçersiz.`);
    assertExactKeys(band, ['minimum', 'label', 'coefficient'], `${context} bandı`);
    const label = requiredString(band, 'label');
    if (labels.has(label)) throw new Error(`${context} bant etiketi tekrarlı.`);
    labels.add(label);
    const isLast = index === bands.length - 1;
    if (band.minimum === null) {
      if (mode !== 'relative' || !isLast) {
        throw new Error(`${context} null alt sınırı yalnız son bağıl bantta olabilir.`);
      }
    } else if (typeof band.minimum !== 'number'
        || !Number.isFinite(band.minimum)
        || band.minimum >= previousMinimum
        || (mode !== 'relative' && (band.minimum < 0 || band.minimum > 100))) {
      throw new Error(`${context} bant alt sınırları kesin azalan ve geçerli olmalıdır.`);
    } else {
      previousMinimum = band.minimum;
    }
    if (mode === 'unweighted') {
      if (band.coefficient !== null) throw new Error(`${context} kredisiz bant katsayı taşıyamaz.`);
    } else if (typeof band.coefficient !== 'number'
        || !Number.isFinite(band.coefficient)
        || band.coefficient < 0
        || band.coefficient > 10) {
      throw new Error(`${context} bant katsayısı geçersiz.`);
    }
  });
  const last = bands[bands.length - 1];
  if (mode !== 'relative' && (!isRecord(last) || last.minimum !== 0)) {
    throw new Error(`${context} mutlak bantlar 0 alt sınırıyla kapanmalıdır.`);
  }
}

function assertRegistryOracle(
  value: unknown,
  kind: string,
  toolId: EducationToolId,
  configVersion: string,
  baselineConfigVersion: string,
): void {
  if (!isRecord(value)) throw new Error(`${toolId} oracle sözleşmesi geçersiz.`);
  assertExactKeys(value, ['kind', 'cases'], `${toolId} oracle`);
  const parsedVersion = parseVersion(configVersion);
  const baselineVersion = parseVersion(baselineConfigVersion);
  if (!parsedVersion || !baselineVersion || compareVersions(parsedVersion, baselineVersion) < 0) {
    throw new Error(`${toolId} oracle config sürümü geçersiz.`);
  }
  // Paketli 1.0.0 örneği geniş pozitif/sınır kapsamını korur. Daha yeni
  // imzalı kurum kaydının asıl doğruluk otoritesi publisher oracle'sıdır;
  // istemci yine en az bir başarı ve bir adversarial vakayı zorunlu tutar.
  const minimumSuccessCount = compareVersions(parsedVersion, baselineVersion) === 0 ? 3 : 1;
  const minimumCaseCount = minimumSuccessCount + 1;
  const cases = arrayValue(value.cases);
  if (value.kind !== kind || cases === null
      || cases.length < minimumCaseCount || cases.length > 32) {
    throw new Error(`${toolId} oracle vaka sayısı/türü geçersiz.`);
  }
  const ids = new Set<string>();
  let successCount = 0;
  let failureCount = 0;
  for (const item of cases) {
    if (!isRecord(item)) throw new Error(`${toolId} oracle vakası geçersiz.`);
    assertExactKeys(item, ['caseId', 'scenario', 'input', 'expected', 'tolerance'], `${toolId} oracle vakası`);
    const caseId = requiredString(item, 'caseId');
    if (!/^[a-z0-9][a-z0-9-]{1,127}$/.test(caseId) || ids.has(caseId)
        || typeof item.scenario !== 'string' || !registryOracleScenarios.has(item.scenario)
        || !isRecord(item.input)
        || typeof item.tolerance !== 'number' || !Number.isFinite(item.tolerance)
        || item.tolerance < 0 || item.tolerance > 1e-6
        || !isRecord(item.expected)) {
      throw new Error(`${toolId} oracle vaka metadata'sı geçersiz.`);
    }
    ids.add(caseId);
    if (item.expected.ok === true) {
      assertExactKeys(item.expected, ['ok', 'value'], `${toolId} oracle başarı sonucu`);
      if (!isRecord(item.expected.value)) throw new Error(`${toolId} oracle başarı değeri geçersiz.`);
      successCount += 1;
    } else if (item.expected.ok === false) {
      assertExactKeys(item.expected, ['ok', 'field', 'errorCode'], `${toolId} oracle hata sonucu`);
      requiredString(item.expected, 'field');
      if (typeof item.expected.errorCode !== 'string'
          || !registryOracleErrorCodes.has(item.expected.errorCode)
          || item.scenario !== 'adversarial') {
        throw new Error(`${toolId} oracle hata sözleşmesi geçersiz.`);
      }
      failureCount += 1;
    } else {
      throw new Error(`${toolId} oracle sonucu success/failure discriminantı taşımıyor.`);
    }
  }
  if (successCount < minimumSuccessCount || failureCount < 1) {
    throw new Error(`${toolId} oracle pozitif/sınır/hata kapsamı eksik.`);
  }
}

function assertRemoteEducationConfig(
  config: Record<string, unknown>,
  toolId: 'relative-grade' | 'grade-average',
  bundled: ToolDefinition,
  configVersion: string,
): void {
  const bundledConfig = bundled.config;
  if (!('calculatorId' in bundledConfig) || config.calculatorId !== bundledConfig.calculatorId) {
    throw new Error(`${toolId} hesaplayıcı kimliği kapalı sözleşmeyle eşleşmiyor.`);
  }
  if (toolId === 'relative-grade') {
    assertExactKeys(config, [
      'kind', 'calculatorId', 'maxScore', 'maxStandardDeviation',
      'tScoreCenter', 'tScoreScale', 'gradeScenario',
    ], `${toolId} config`);
    if (config.maxScore !== 100 || config.maxStandardDeviation !== 100
        || config.tScoreCenter !== 50 || config.tScoreScale !== 10) {
      throw new Error(`${toolId} güvenlik sınırları geçersiz.`);
    }
    assertGradeScenario(config.gradeScenario, 'relative', `${toolId} bağıl`);
  } else {
    assertExactKeys(config, [
      'kind', 'calculatorId', 'maxScore', 'maxCourses', 'maxCredits',
      'weightedGradeScenario', 'unweightedBandScenario',
    ], `${toolId} config`);
    if (config.maxScore !== 100 || config.maxCourses !== 100 || config.maxCredits !== 10) {
      throw new Error(`${toolId} not/ders/kredi sınırları geçersiz.`);
    }
    assertGradeScenario(config.weightedGradeScenario, 'weighted', `${toolId} kredili`);
    assertGradeScenario(config.unweightedBandScenario, 'unweighted', `${toolId} kredisiz`);
  }

  // 1.0.0 yalnız açıkça "örnek" paket profilidir. İmzalı daha yeni bir
  // sürüm ancak kurum profili olabilir; böylece sürüm yükseltip örnek etiketiyle
  // sessiz eşik değiştirme veya 1.0.0 altında kurum kuralı yayımlama kapanır.
  const expectedKind = configVersion === bundled.configVersion ? 'example' : 'institution';
  const scenarios = toolId === 'relative-grade'
    ? [config.gradeScenario]
    : [config.weightedGradeScenario, config.unweightedBandScenario];
  if (scenarios.some((scenario) => !isRecord(scenario) || scenario.kind !== expectedKind)) {
    throw new Error(`${toolId} ${configVersion} sürümü yalnız ${expectedKind} senaryosu taşımalıdır.`);
  }
}

function assertEducationToolDefinition(
  candidate: Record<string, unknown>,
  toolId: EducationToolId,
): void {
  const bundled = bundledToolDefinition(toolId);
  if (!isRecord(candidate.category)
      || candidate.slug !== bundled.slug
      || candidate.path !== bundled.path
      || candidate.classification !== bundled.classification
      || candidate.category.id !== 'egitim-sinav'
      || candidate.category.label !== 'Eğitim ve Sınav') {
    throw new Error(`${toolId} kimlik/rota/kategori sözleşmesi geçersiz.`);
  }
  for (const field of ['inputSchema', 'resultSchema'] as const) {
    assertCanonicalField(candidate, bundled, field, toolId);
  }
  if (candidate.classification !== 'remote-config') {
    if (candidate.configVersion !== bundled.configVersion) {
      throw new Error(`${toolId} timeless config sürümü kapalı baseline ile eşleşmiyor.`);
    }
    for (const field of ['config', 'testVectors', 'sources', 'updateCadence'] as const) {
      assertCanonicalField(candidate, bundled, field, toolId);
    }
    return;
  }
  if (toolId !== 'relative-grade' && toolId !== 'grade-average') {
    throw new Error(`${toolId} desteklenmeyen remote-config sınıfı taşıyor.`);
  }
  assertRemoteLifecycle(candidate, toolId);
  assertRemoteSources(candidate, toolId);
  if (!isRecord(candidate.config)) throw new Error(`${toolId} config zarfı geçersiz.`);
  assertRemoteEducationConfig(
    candidate.config,
    toolId,
    bundled,
    requiredString(candidate, 'configVersion'),
  );
  assertRegistryOracle(
    candidate.testVectors,
    'education-oracle-v1',
    toolId,
    requiredString(candidate, 'configVersion'),
    bundled.configVersion,
  );
}

function assertEverydayHealthToolDefinition(
  candidate: Record<string, unknown>,
  toolId: EverydayHealthToolId,
): void {
  const bundled = bundledToolDefinition(toolId);
  const expectedCategory = toolId === 'profit-margin'
    ? ['ticari-muhasebe', 'Ticari ve Muhasebe']
    : toolId === 'basal-metabolic-rate' || toolId === 'historical-ideal-weight-reference'
      ? ['saglik-yasam', 'Sağlık ve Yaşam']
      : ['arac-emlak-yasam', 'Araç, Emlak ve Yaşam'];
  const expectedClassification = 'timeless';
  if (!isRecord(candidate.category)
      || candidate.slug !== bundled.slug
      || candidate.path !== bundled.path
      || candidate.category.id !== expectedCategory[0]
      || candidate.category.label !== expectedCategory[1]
      || candidate.classification !== expectedClassification
      || candidate.schemaVersion !== bundled.schemaVersion
      || candidate.contentVersion !== bundled.contentVersion
      || candidate.configVersion !== bundled.configVersion
      || candidate.effectiveFrom !== bundled.effectiveFrom
      || candidate.effectiveTo !== bundled.effectiveTo
      || candidate.minAppVersion !== bundled.minAppVersion
      || candidate.lastReviewed !== bundled.lastReviewed) {
    throw new Error(`${toolId} kimlik/rota/kategori/lifecycle sözleşmesi geçersiz.`);
  }

  const sources = arrayValue(candidate.sources);
  const sourceOptional = sourceOptionalEverydayHealthToolIds.has(toolId);
  if (sources === null
      || (sourceOptional && sources.length !== 0)
      || (!sourceOptional && sources.length === 0)) {
    throw new Error(`${toolId} kaynak allowlist sözleşmesi geçersiz.`);
  }

  const content = candidate.content;
  const limitations = isRecord(content) ? arrayValue(content.limitations) : null;
  const panel = isRecord(content) ? content.sourcePanel : null;
  const panelSourceIds = isRecord(panel) ? arrayValue(panel.sourceIds) : null;
  if (!isRecord(content)
      || limitations === null
      || limitations.length === 0
      || limitations.some((item) => typeof item !== 'string' || item.trim() === '')
      || !isRecord(content.sourcePanel)
      || panelSourceIds === null) {
    throw new Error(`${toolId} görünür kapsam/kaynak paneli eksik.`);
  }
  const sourceIds = sources.map((source) => isRecord(source) ? source.sourceId : undefined);
  if (!sameOrder(panelSourceIds, sourceIds)) {
    throw new Error(`${toolId} görünür kaynak paneli registry kaynaklarıyla eşleşmiyor.`);
  }

  const inputFields = schemaFields(candidate.inputSchema, `${toolId} inputSchema`);
  if (inputFields.some((field) => field.id === 'toolId')) {
    throw new Error(`${toolId} iç araç kimliğini kullanıcı form alanı yapamaz.`);
  }
  if (toolId === 'basal-metabolic-rate') {
    const formula = inputFields.find((field) => field.id === 'formula');
    const options = isRecord(formula) ? arrayValue(formula.options) : null;
    const values = options !== null
      ? options.map((option) => isRecord(option) ? option.value : undefined)
      : [];
    if (!sameOrder(values, ['mifflin', 'harris'])) {
      throw new Error('basal-metabolic-rate formula allowlist sözleşmesi geçersiz.');
    }
  }
  if (toolId === 'historical-ideal-weight-reference') {
    const currentWeight = inputFields.find((field) => field.id === 'currentWeightKg');
    const resultFields = schemaFields(candidate.resultSchema, `${toolId} resultSchema`);
    const currentWeightResult = resultFields.find((field) => field.id === 'currentWeightKg');
    if (!currentWeight
        || currentWeight.required !== false
        || currentWeight.defaultValue !== null
        || !currentWeightResult
        || currentWeightResult.nullable !== true) {
      throw new Error('historical-ideal-weight-reference opsiyonel kilo sözleşmesi geçersiz.');
    }
  }

  // Exact46 onay kökü bu beş sözleşmeyi config/vector/content/source birlikte
  // oynatılsa dahi kapalı tutar. Yeni yöntem ancak yeni uygulama ve yeni trust
  // köküyle kabul edilir; imza tek başına motor davranışını genişletemez.
  for (const field of [
    'config', 'inputSchema', 'resultSchema', 'testVectors', 'sources',
    'updateCadence', 'content',
  ] as const) assertCanonicalField(candidate, bundled, field, toolId);
}

const sourcePreparedMutableProjectionValue = '<signed-source-prepared-renewal>';
const sourcePreparedLifecycleKeys = [
  'configVersion', 'effectiveFrom', 'effectiveTo', 'reviewAfter', 'reviewedOn', 'sourceIds',
] as const;
const sourcePreparedSourceKeys = [
  'sourceId', 'sourceGroupId', 'title', 'publisher', 'url', 'kind', 'reviewedOn', 'watchFields',
] as const;

function sourcePreparedConfigShape(value: unknown): unknown {
  const entries = arrayValue(value);
  if (entries !== null) {
    return {
      arrayItemShapes: [...new Set(
        entries.map((item) => canonicalRegistryJson(sourcePreparedConfigShape(item))),
      )].sort(),
    };
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.keys(value).sort().map((key) => [key, sourcePreparedConfigShape(value[key])]),
    );
  }
  return sourcePreparedMutableProjectionValue;
}

function sourcePreparedContentImmutableProjection(value: unknown): unknown {
  if (!isRecord(value) || !isRecord(value.sourcePanel)) return value;
  const panel = value.sourcePanel;
  return {
    ...value,
    sourcePanel: {
      ...panel,
      sourceIds: sourcePreparedMutableProjectionValue,
      ...(panel.nextReviewOn === undefined
        ? {}
        : { nextReviewOn: sourcePreparedMutableProjectionValue }),
    },
  };
}

function sourcePreparedImmutableProjection(value: unknown): unknown {
  if (!isRecord(value)) throw new Error('Prepared araç zarfı geçersiz.');
  const cadence = isRecord(value.updateCadence) ? value.updateCadence : {};
  const config = isRecord(value.config) ? value.config : {};
  const testVectors = isRecord(value.testVectors) ? value.testVectors : {};
  return {
    toolId: value.toolId,
    slug: value.slug,
    path: value.path,
    category: value.category,
    classification: value.classification,
    engineId: value.engineId,
    schemaVersion: value.schemaVersion,
    contentVersion: value.contentVersion,
    minAppVersion: value.minAppVersion,
    updateCadence: {
      mode: cadence.mode,
      days: cadence.days,
      specialMonths: cadence.specialMonths,
      note: cadence.note,
    },
    inputSchema: value.inputSchema,
    resultSchema: value.resultSchema,
    content: sourcePreparedContentImmutableProjection(value.content),
    configContract: {
      kind: config.kind,
      calculatorId: config.calculatorId,
      shape: sourcePreparedConfigShape(config),
    },
    testVectorKind: testVectors.kind,
  };
}

function sourcePreparedAllowedKinds(kind: unknown): ReadonlySet<string> {
  if (kind === 'vehicle-finance-oracle-v1' || kind === 'commercial-oracle-v1') {
    return new Set(['official-government', 'official-operator']);
  }
  if (kind === 'source-education-oracle-v1') {
    return new Set(['official-government', 'authoritative-education']);
  }
  if (kind === 'date-health-oracle-v1') {
    return new Set([
      'official-government', 'official-operator',
      'peer-reviewed-medical-research', 'international-standards-body',
    ]);
  }
  return new Set();
}

function assertSourcePreparedOracle(
  candidate: Record<string, unknown>,
  bundled: ToolDefinition,
  toolId: SourcePreparedToolId,
): void {
  if (!isRecord(candidate.testVectors) || !isRecord(bundled.testVectors)) {
    throw new Error(`${toolId} prepared oracle sözleşmesi geçersiz.`);
  }
  const vectors = candidate.testVectors;
  assertExactKeys(vectors, ['kind', 'cases'], `${toolId} prepared oracle`);
  const cases = arrayValue(vectors.cases);
  if (vectors.kind !== bundled.testVectors.kind
      || cases === null
      || cases.length < 2
      || cases.length > 32) {
    throw new Error(`${toolId} prepared oracle türü/vaka sayısı geçersiz.`);
  }
  const inputFields = schemaFields(candidate.inputSchema, `${toolId} inputSchema`);
  const resultFields = schemaFields(candidate.resultSchema, `${toolId} resultSchema`);
  const inputIds = inputFields.map((field) => requiredString(field, 'id'));
  const resultIds = resultFields.map((field) => requiredString(field, 'id'));
  const caseIds = new Set<string>();
  let successCount = 0;
  let adversarialCount = 0;

  for (const testCase of cases) {
    if (!isRecord(testCase)) throw new Error(`${toolId} prepared oracle vakası geçersiz.`);
    assertExactKeys(
      testCase,
      ['caseId', 'scenario', 'input', 'expected', 'tolerance'],
      `${toolId} prepared oracle vakası`,
    );
    const caseId = requiredString(testCase, 'caseId');
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(caseId)
        || caseIds.has(caseId)
        || typeof testCase.scenario !== 'string'
        || !registryOracleScenarios.has(testCase.scenario)
        || !isRecord(testCase.input)
        || Object.keys(testCase.input).some((fieldId) => !inputIds.includes(fieldId))
        || typeof testCase.tolerance !== 'number'
        || !Number.isFinite(testCase.tolerance)
        || testCase.tolerance <= 0
        || testCase.tolerance > 0.001
        || !isRecord(testCase.expected)) {
      throw new Error(`${toolId} prepared oracle vaka metadata'sı geçersiz.`);
    }
    caseIds.add(caseId);
    // Yukarıdaki kapıdan sonra closure içinde de record daraltmasını koru.
    const caseInput = testCase.input;

    if (testCase.expected.ok === true) {
      assertExactKeys(testCase.expected, ['ok', 'value'], `${toolId} prepared oracle başarısı`);
      if (testCase.scenario === 'adversarial'
          || !isRecord(testCase.expected.value)
          || Object.keys(testCase.expected.value).length === 0
          || Object.keys(testCase.expected.value).some((fieldId) => !resultIds.includes(fieldId))) {
        throw new Error(`${toolId} prepared oracle başarı zarfı geçersiz.`);
      }
      const missingRequired = inputFields.find((field) => {
        if (field.required !== true) return false;
        const visibleWhen = isRecord(field.visibleWhen) ? field.visibleWhen : null;
        const visible = visibleWhen === null
          || caseInput[String(visibleWhen.fieldId)] === visibleWhen.equals;
        return visible && !Object.prototype.hasOwnProperty.call(caseInput, requiredString(field, 'id'));
      });
      if (missingRequired) throw new Error(`${toolId} prepared başarı oracle'ı zorunlu girdiyi taşımıyor.`);
      successCount += 1;
    } else if (testCase.expected.ok === false) {
      assertExactKeys(testCase.expected, ['ok', 'field', 'errorCode'], `${toolId} prepared oracle hatası`);
      const field = requiredString(testCase.expected, 'field');
      const rootField = field.replace(/^input\./u, '').split(/[.\[]/u)[0];
      if (testCase.scenario !== 'adversarial'
          || rootField === undefined
          || !inputIds.includes(rootField)
          || typeof testCase.expected.errorCode !== 'string'
          || !registryOracleErrorCodes.has(testCase.expected.errorCode)) {
        throw new Error(`${toolId} prepared adversarial oracle zarfı geçersiz.`);
      }
      adversarialCount += 1;
    } else {
      throw new Error(`${toolId} prepared oracle başarı/hata discriminantı taşımıyor.`);
    }
  }
  if (successCount < 1 || adversarialCount < 1) {
    throw new Error(`${toolId} prepared oracle başarı/adversarial kapsamı eksik.`);
  }
}

function assertSourcePreparedToolDefinition(
  candidate: Record<string, unknown>,
  toolId: SourcePreparedToolId,
): void {
  const bundled = bundledToolDefinition(toolId);
  assertExactKeys(candidate, Object.keys(bundled), `${toolId} ToolDefinition`);
  const currentVersion = parseVersion(requiredString(candidate, 'configVersion'));
  const baselineVersion = parseVersion(bundled.configVersion);
  const versionOrder = currentVersion === null || baselineVersion === null ? null : compareVersions(currentVersion, baselineVersion);
  if (versionOrder === null || versionOrder < 0 || (toolId === 'countdown' && versionOrder !== 0)) {
    throw new Error(toolId === 'countdown'
      ? `${toolId} zamansız configVersion exact ${bundled.configVersion} olmalıdır.`
      : `${toolId} configVersion strict semver ve baseline ${bundled.configVersion} veya yeni olmalıdır.`);
  }
  if (versionOrder === 0) {
    if (canonicalRegistryJson(candidate) !== canonicalRegistryJson(bundled)) {
      throw new Error(`${toolId} prepared baseline ToolDefinition paketli onayla birebir eşleşmiyor.`);
    }
    return;
  }
  if (
    canonicalRegistryJson(sourcePreparedImmutableProjection(candidate))
    !== canonicalRegistryJson(sourcePreparedImmutableProjection(bundled))
  ) {
    throw new Error(`${toolId} prepared ileri sürümü motor/descriptor/enum/içerik/config şeklini değiştiremez.`);
  }

  const config = candidate.config;
  const lifecycle = isRecord(config) ? config.policyLifecycle : null;
  const cadence = candidate.updateCadence;
  const sources = arrayValue(candidate.sources);
  if (!isRecord(config)
      || (!('calculatorId' in bundled.config) || config.calculatorId !== bundled.config.calculatorId)
      || !isRecord(lifecycle)
      || !isRecord(cadence)
      || sources === null) {
    throw new Error(`${toolId} prepared config/lifecycle zarfı geçersiz.`);
  }
  // İleri sürüm yalnız takvim değerini yenileyebilir; cadence zarfına yeni ve
  // istemcinin anlamını bilmediği bir davranış anahtarı eklenemez.
  assertExactKeys(
    cadence,
    Object.keys(bundled.updateCadence),
    `${toolId} prepared updateCadence`,
  );
  assertExactKeys(lifecycle, sourcePreparedLifecycleKeys, `${toolId} prepared lifecycle`);
  const sourceIds = sources.map((source) => isRecord(source) ? source.sourceId : undefined);
  const lifecycleSourceIds = arrayValue(lifecycle.sourceIds);
  if (lifecycle.configVersion !== candidate.configVersion
      || lifecycle.effectiveFrom !== candidate.effectiveFrom
      || lifecycle.effectiveTo !== candidate.effectiveTo
      || lifecycle.reviewedOn !== candidate.lastReviewed
      || lifecycle.reviewAfter !== cadence.reviewAfter
      || lifecycleSourceIds === null
      || !sameOrder(lifecycleSourceIds, sourceIds)
      || !isoDate(lifecycle.effectiveFrom)
      || !isoDate(lifecycle.reviewedOn)
      || lifecycle.reviewedOn < lifecycle.effectiveFrom
      || (lifecycle.effectiveTo !== null && (!isoDate(lifecycle.effectiveTo) || lifecycle.effectiveTo < lifecycle.effectiveFrom))
      || !isoDate(lifecycle.reviewAfter)
      || lifecycle.reviewAfter <= lifecycle.reviewedOn
      || requiredString(candidate, 'lastReviewed') < bundled.lastReviewed) {
    throw new Error(`${toolId} prepared lifecycle sürüm/tarih/sourceId bağı geçersiz.`);
  }

  const preparedVectors = isRecord(candidate.testVectors) ? candidate.testVectors : null;
  const allowedKinds = sourcePreparedAllowedKinds(preparedVectors?.kind);
  const seenSourceIds = new Set<string>();
  for (const source of sources) {
    if (!isRecord(source)) throw new Error(`${toolId} prepared kaynak kaydı geçersiz.`);
    assertExactKeys(source, sourcePreparedSourceKeys, `${toolId} prepared kaynak`);
    const sourceId = requiredString(source, 'sourceId');
    const watchFields = arrayValue(source.watchFields);
    if (!/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/.test(sourceId)
        || seenSourceIds.has(sourceId)
        || (source.sourceGroupId !== null
          && (typeof source.sourceGroupId !== 'string' || source.sourceGroupId.trim() === ''))
        || requiredString(source, 'title').trim() === ''
        || requiredString(source, 'publisher').trim() === ''
        || !requiredString(source, 'url').startsWith('https://')
        || typeof source.kind !== 'string'
        || !allowedKinds.has(source.kind)
        || source.reviewedOn !== lifecycle.reviewedOn
        || watchFields === null
        || watchFields.length === 0
        || watchFields.some((field) => typeof field !== 'string' || field.trim() === '')) {
      throw new Error(`${toolId} prepared kaynak provenance sözleşmesi geçersiz.`);
    }
    seenSourceIds.add(sourceId);
  }

  const content = candidate.content;
  const panel = isRecord(content) ? content.sourcePanel : null;
  const panelSourceIds = isRecord(panel) ? arrayValue(panel.sourceIds) : null;
  if (!isRecord(panel)
      || panelSourceIds === null
      || !sameOrder(panelSourceIds, sourceIds)
      || (panel.nextReviewOn !== undefined && panel.nextReviewOn !== cadence.reviewAfter)) {
    throw new Error(`${toolId} prepared görünür kaynak paneli lifecycle ile eşleşmiyor.`);
  }
  assertSourcePreparedOracle(candidate, bundled, toolId);
}

/**
 * Tarayıcı IIFE'sine AJV runtime compiler sokmadan, çalıştırılacak 108 kapalı
 * sözleşmenin güvenlik-kritik alanlarını doğrular. Ayrıntılı içerik şeması
 * publisher ve tool-registry build kapısında AJV ile ayrıca doğrulanır.
 */
function assertBrowserToolDefinitions(value: unknown): asserts value is readonly ToolDefinition[] {
  assertSafeJsonTree(value);
  const entries = arrayValue(value);
  if (entries === null || entries.length !== toolIds.length) {
    throw new Error(`Registry ${toolIds.length} aracın tamamını içermiyor.`);
  }
  const seen = new Set<string>();
  for (const [index, candidate] of entries.entries()) {
    assertRegistryContract(candidate);
    if (!isRecord(candidate) || !isRecord(candidate.config)) {
      throw new Error('Registry araç kaydı geçersiz.');
    }
    const rawToolId = requiredString(candidate, 'toolId');
    if (!isSupportedToolId(rawToolId) || seen.has(rawToolId)) {
      throw new Error(`Registry araç kimliği bilinmiyor veya tekrarlı: ${rawToolId}.`);
    }
    const toolId = rawToolId;
    seen.add(toolId);
    // Exact108 sırası görünür katalog sırasıdır ve imzalı payload sözleşmesinin
    // parçasıdır; yetkili bir imzalayıcı dahi bu UI davranışını sessizce
    // değiştiremez. Sıralama yaparak normalleştirmek yerine sapmayı reddederiz.
    if (toolId !== toolIds[index]) {
      throw new Error(`Registry araçları kanonik exact108 sırasıyla eşleşmiyor: ${toolId}.`);
    }
    const contract = contracts[toolId];
    if (!contract) throw new Error(`${toolId} motor/config sözleşmesi bulunamadı.`);
    const [engineId, configKind] = contract;
    const bundled = bundledToolDefinition(toolId);
    if (
      candidate.schemaVersion !== bundled.schemaVersion
      || candidate.engineId !== engineId
      || candidate.config.kind !== configKind
    ) {
      throw new Error(`${toolId} için yeni şema/motor/config uygulama güncellemesi gerektiriyor.`);
    }
    for (const field of ['contentVersion', 'configVersion', 'lastReviewed'] as const) {
      requiredString(candidate, field);
    }
    if (!isoDate(candidate.effectiveFrom) || (candidate.effectiveTo !== null && !isoDate(candidate.effectiveTo))) {
      throw new Error(`${toolId} yürürlük tarihi geçersiz.`);
    }
    if (candidate.effectiveTo !== null && candidate.effectiveTo < candidate.effectiveFrom) {
      throw new Error(`${toolId} yürürlük aralığı ters.`);
    }
    const minimumVersion = requiredString(candidate, 'minAppVersion');
    if (!versionSupported(minimumVersion)) {
      throw new Error(`${toolId} için web istemcisinin en az ${minimumVersion} sürümü gerekiyor.`);
    }
    const mayOmitSources = (isEverydayHealthToolId(toolId)
      && sourceOptionalEverydayHealthToolIds.has(toolId))
      // Countdown bilinçli olarak resmî politika kaynağı taşımayan, yalnız
      // kullanıcının tarihine göre çalışan zamansız bir araçtır.
      || (isSourcePreparedToolId(toolId) && toolId === 'countdown');
    const sources = arrayValue(candidate.sources);
    if (sources === null
        || (sources.length === 0 && !mayOmitSources)) {
      throw new Error(`${toolId} resmî kaynak kaydı içermiyor.`);
    }
    for (const source of sources) {
      if (!isRecord(source) || !requiredString(source, 'url').startsWith('https://') || !isoDate(source.reviewedOn)) {
        throw new Error(`${toolId} kaynak kaydı geçersiz.`);
      }
    }

    if (toolId === 'percentage') {
      numberIn(candidate.config, 'roundingScale', 0, 8);
      const modes = arrayValue(candidate.config.supportedModes);
      if (modes === null || modes.join('|') !== 'percentage-of|what-percentage|increase-decrease|percentage-change') {
        throw new Error('Yüzde mod sözleşmesi geçersiz.');
      }
    } else if (toolId === 'salary') {
      if (candidate.config.monthsPerYear !== 12 || !isArray(candidate.config.incomeTaxBrackets)) {
        throw new Error('Maaş yıllık kural sözleşmesi geçersiz.');
      }
      for (const field of [
        'minimumGrossWage', 'socialSecurityCeilingMultiplier',
      ]) numberIn(candidate.config, field, Number.MIN_VALUE, Number.MAX_SAFE_INTEGER);
      for (const field of [
        'socialSecurityEmployeeRate', 'unemploymentEmployeeRate', 'stampTaxRate',
      ]) numberIn(candidate.config, field, 0, 1);
      if (!isRecord(candidate.config.netToGrossIteration)) throw new Error('Maaş ters hesap kuralı eksik.');
      numberIn(candidate.config.netToGrossIteration, 'maxIterations', 1, 10_000);
      numberIn(candidate.config.netToGrossIteration, 'tolerance', Number.MIN_VALUE, 1_000);
      numberIn(candidate.config.netToGrossIteration, 'initialMultiplier', Number.MIN_VALUE, 100);
      numberIn(candidate.config.netToGrossIteration, 'correctionFactor', Number.MIN_VALUE, 100);
    } else if (toolId === 'hgs-toll') {
      const version = parseVersion(requiredString(candidate, 'configVersion'));
      const baselineVersion = parseVersion(bundled.configVersion);
      if (!version || !baselineVersion || compareVersions(version, baselineVersion) < 0) {
        throw new Error('HGS tarife sürümü geçersiz veya eski.');
      }
      if (!isoDate(candidate.lastReviewed)
          || !isRecord(candidate.updateCadence)
          || !isoDate(candidate.updateCadence.reviewAfter)
          || candidate.updateCadence.reviewAfter <= candidate.lastReviewed) {
        throw new Error('HGS inceleme tarihi geçersiz.');
      }
      if (candidate.config.currency !== 'TRY' || candidate.config.vatIncluded !== true) {
        throw new Error('HGS para birimi/KDV sözleşmesi geçersiz.');
      }
      numberIn(candidate.config, 'roundTripMultiplier', Number.MIN_VALUE, 100);
      const classes = arrayValue(candidate.config.vehicleClasses);
      const crossings = arrayValue(candidate.config.crossings);
      if (classes === null || classes.length !== 6 || crossings === null || crossings.length !== 6) {
        throw new Error('HGS sınıf veya geçiş matrisi eksik.');
      }
      const crossingIds = new Set<string>();
      for (const crossing of crossings) {
        if (!isRecord(crossing) || !isoDate(crossing.effectiveFrom)) throw new Error('HGS geçiş kaydı geçersiz.');
        const crossingId = requiredString(crossing, 'id');
        if (crossingIds.has(crossingId)) throw new Error('HGS geçiş kimliği tekrarlı.');
        crossingIds.add(crossingId);
        assertPriceMap(crossing.pricesByVehicleClass);
        if (crossing.nightPricesByVehicleClass !== null) assertPriceMap(crossing.nightPricesByVehicleClass);
      }
      const requiredCrossings = ['15-temmuz', 'fsm', 'yss', 'osmangazi', '1915-canakkale', 'avrasya'];
      if (requiredCrossings.some((id) => !crossingIds.has(id))) throw new Error('HGS geçiş matrisi eksik.');
    } else if (isUnitToolId(toolId)) {
      assertUnitToolDefinition(candidate, toolId);
    } else if (isMathToolId(toolId)) {
      assertMathToolDefinition(candidate, toolId);
    } else if (isHomeToolId(toolId)) {
      assertHomeToolDefinition(candidate, toolId);
    } else if (isDateTimeToolId(toolId)) {
      assertDateTimeToolDefinition(candidate, toolId);
    } else if (isEducationToolId(toolId)) {
      assertEducationToolDefinition(candidate, toolId);
    } else if (isEverydayHealthToolId(toolId)) {
      assertEverydayHealthToolDefinition(candidate, toolId);
    } else if (isFinanceBusinessToolId(toolId)) {
      assertFinanceBusinessToolDefinition(candidate, toolId);
    } else if (isSourcePreparedToolId(toolId)) {
      assertSourcePreparedToolDefinition(candidate, toolId);
    } else {
      const exhaustive: never = toolId;
      throw new Error(`Registry araç sözleşmesi tanımsız: ${exhaustive}.`);
    }
  }
}

function decodeBase64(value: string): Uint8Array {
  if (value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
    throw new Error('Registry Base64 verisi geçersiz.');
  }
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const bytes: number[] = [];
  for (let index = 0; index < value.length; index += 4) {
    const chunk = value.slice(index, index + 4);
    const values = [chunk[0], chunk[1], chunk[2], chunk[3]].map((character) =>
      character === '=' ? 0 : alphabet.indexOf(character ?? ''));
    if (values.some((item) => item < 0)) throw new Error('Registry Base64 verisi geçersiz.');
    const packed = (values[0] << 18) | (values[1] << 12) | (values[2] << 6) | values[3];
    bytes.push((packed >> 16) & 0xff);
    if (chunk[2] !== '=') bytes.push((packed >> 8) & 0xff);
    if (chunk[3] !== '=') bytes.push(packed & 0xff);
  }
  return Uint8Array.from(bytes);
}

async function verifyEd25519(canonical: string, signature: string, publicKey: string): Promise<boolean> {
  const signatureBytes = decodeBase64(signature);
  const keyBytes = decodeBase64(publicKey);
  if (signatureBytes.length !== nacl.sign.signatureLength || keyBytes.length !== nacl.sign.publicKeyLength) {
    throw new Error('Registry Ed25519 imza veya anahtar uzunluğu geçersiz.');
  }
  return nacl.sign.detached.verify(new TextEncoder().encode(canonical), signatureBytes, keyBytes);
}

async function sha256(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error('Tarayıcı SHA-256 desteği bulunamadı.');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function parsePointer(value: unknown, now: Date, allowExpired: boolean): ToolRegistryCurrentPointer {
  const entries = isRecord(value) ? arrayValue(value.tools) : null;
  if (!isRecord(value) || value.apiVersion !== 'v1' || entries === null) {
    throw new Error('Registry current pointer biçimi geçersiz.');
  }
  const seen = new Set<BrowserToolId>();
  const tools = entries.map((candidate, index) => {
    if (!isRecord(candidate)) throw new Error('Registry araç pointer kaydı geçersiz.');
    const rawToolId = requiredString(candidate, 'toolId');
    if (!isSupportedToolId(rawToolId) || seen.has(rawToolId)) {
      throw new Error('Registry pointer araç kimliği geçersiz.');
    }
    const toolId = rawToolId;
    seen.add(toolId);
    if (toolId !== toolIds[index]) {
      throw new Error(`Registry pointer araçları kanonik exact108 sırasıyla eşleşmiyor: ${toolId}.`);
    }
    const url = requiredString(candidate, 'url');
    if (!/^[A-Za-z0-9._/-]+\.json$/.test(url) || url.startsWith('/') || url.includes('..')) {
      throw new Error(`${toolId} değişmez URL yolu güvenli değil.`);
    }
    const contentSha256 = requiredString(candidate, 'contentSha256');
    if (!/^[a-f0-9]{64}$/.test(contentSha256)) throw new Error(`${toolId} SHA-256 değeri geçersiz.`);
    return {
      toolId,
      url,
      contentSha256,
      schemaVersion: requiredString(candidate, 'schemaVersion'),
      contentVersion: requiredString(candidate, 'contentVersion'),
      configVersion: requiredString(candidate, 'configVersion'),
    } satisfies ToolVersionPointer;
  });
  if (seen.size !== toolIds.length || toolIds.some((id) => !seen.has(id))) {
    throw new Error(`Registry pointer ${toolIds.length} aracın tamamını içermiyor.`);
  }
  const sequence = value.sequence;
  if (typeof sequence !== 'number' || !Number.isSafeInteger(sequence) || sequence <= 0) {
    throw new Error('Registry sequence değeri geçersiz.');
  }
  const activatedAt = exactIsoTimestamp(value.activatedAt, 'activatedAt');
  const expiresAt = exactIsoTimestamp(value.expiresAt, 'expiresAt');
  const activatedTime = Date.parse(activatedAt);
  const expiresTime = Date.parse(expiresAt);
  if (expiresTime <= activatedTime || expiresTime - activatedTime > maximumValidityMs) {
    throw new Error('Registry manifest geçerlilik penceresi geçersiz.');
  }
  if (activatedTime > now.getTime() + maximumActivationClockSkewMs) {
    throw new Error('Registry aktivasyon zamanı izin verilen saat farkından ileride.');
  }
  if (!allowExpired && expiresTime <= now.getTime()) throw new Error('Registry manifest tazelik süresi dolmuş.');
  if (!isRecord(value.signature) || value.signature.algorithm !== 'Ed25519') {
    throw new Error('Registry Ed25519 imzası eksik.');
  }
  return {
    apiVersion: 'v1', releaseId: requiredString(value, 'releaseId'), activatedAt, expiresAt, sequence, tools,
    signature: {
      algorithm: 'Ed25519',
      keyId: requiredString(value.signature, 'keyId'),
      valueBase64: requiredString(value.signature, 'valueBase64'),
    },
  };
}

async function readLimitedJson(response: Response, maximumBytes: number): Promise<unknown> {
  if (!response.ok) throw new Error(`Registry isteği HTTP ${response.status} ile başarısız oldu.`);
  const declared = response.headers.get('content-length');
  if (declared && /^\d+$/.test(declared) && Number(declared) > maximumBytes) {
    throw new Error('Registry yanıtı izin verilen boyutu aşıyor.');
  }
  let bytes: Uint8Array;
  if (response.body) {
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    while (true) {
      const item = await reader.read();
      if (item.done) break;
      total += item.value.byteLength;
      if (total > maximumBytes) {
        await reader.cancel();
        throw new Error('Registry yanıtı izin verilen boyutu aşıyor.');
      }
      chunks.push(item.value);
    }
    bytes = new Uint8Array(total);
    let offset = 0;
    chunks.forEach((chunk) => { bytes.set(chunk, offset); offset += chunk.byteLength; });
  } else {
    bytes = new TextEncoder().encode(await response.text());
    if (bytes.byteLength > maximumBytes) throw new Error('Registry yanıtı izin verilen boyutu aşıyor.');
  }
  try {
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    throw new Error('Registry yanıtı geçerli UTF-8 JSON değil.');
  }
}

function endpointUrl(endpoint: string, allowLocalhostHttp: boolean): URL {
  const browserLocation = globalThis.location;
  const base = browserLocation?.href;
  const url = new URL(endpoint, base);
  const loopback = ['127.0.0.1', 'localhost', '::1', '[::1]'].includes(url.hostname);
  if (url.protocol !== 'https:' && !(allowLocalhostHttp && url.protocol === 'http:' && loopback)) {
    throw new Error('Registry endpoint üretimde HTTPS olmalıdır.');
  }
  return url;
}

function toolUrl(currentUrl: URL, relative: string): URL {
  const directory = new URL('./', currentUrl);
  const resolved = new URL(relative, directory);
  if (resolved.origin !== currentUrl.origin || !resolved.pathname.startsWith(directory.pathname)) {
    throw new Error('Registry araç URL yolu API dizini dışına çıkıyor.');
  }
  return resolved;
}

async function verifyManifest(
  manifest: ToolRegistryCurrentPointer,
  keyId: string,
  publicKey: string,
  verifier: SignatureVerifier,
): Promise<void> {
  if (manifest.signature.keyId !== keyId) throw new Error('Registry keyId pini eşleşmiyor.');
  if (!await verifier(canonicalUnsignedToolRegistryPointer(manifest), manifest.signature.valueBase64, publicKey)) {
    throw new Error('Registry manifest imzası doğrulanamadı.');
  }
}

async function verifyTools(
  manifest: ToolRegistryCurrentPointer,
  tools: readonly unknown[],
  digest: Digest,
): Promise<readonly ToolDefinition[]> {
  const definitions = parseBrowserToolDefinitions(tools);
  for (const tool of definitions) {
    const pointer = manifest.tools.find((item) => item.toolId === tool.toolId);
    if (!pointer) throw new Error(`${tool.toolId} pointer kaydı eksik.`);
    if (
      await digest(canonicalRegistryJson(tool)) !== pointer.contentSha256
      || tool.schemaVersion !== pointer.schemaVersion
      || tool.contentVersion !== pointer.contentVersion
      || tool.configVersion !== pointer.configVersion
    ) throw new Error(`${tool.toolId} hash veya sürüm alanları eşleşmiyor.`);
  }
  return definitions;
}

export async function fetchSignedRegistrySnapshot(
  options: SignedRegistryFetchOptions,
): Promise<VerifiedWebRegistrySnapshot> {
  const currentUrl = endpointUrl(options.endpoint, options.allowLocalhostHttp === true);
  const fetcher = options.fetchImplementation ?? fetch;
  const digest = options.digestImplementation ?? sha256;
  const verifier = options.verifySignatureImplementation ?? verifyEd25519;
  const now = (options.now ?? (() => new Date()))();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 8_000);
  try {
    const currentValue = await readLimitedJson(
      await fetcher(currentUrl, { headers: { Accept: 'application/json' }, signal: controller.signal }),
      maximumPointerBytes,
    );
    const current = parsePointer(currentValue, now, false);
    await verifyManifest(current, options.pinnedKeyId, options.pinnedPublicKeyBase64, verifier);
    const cached = options.cachedSnapshot;
    if (cached && current.sequence < cached.sequence) throw new Error('Registry replay girişimi reddedildi.');
    if (cached && current.sequence === cached.sequence) {
      if (canonicalRegistryJson(current) !== canonicalRegistryJson(cached.manifest)) {
        throw new Error('Aynı sequence için farklı manifest reddedildi.');
      }
      return { ...cached, fetchedAt: now.toISOString() };
    }
    if (!cached && current.sequence <= (options.minimumSequenceExclusive ?? 0)) {
      throw new Error('Registry sequence paket eşiğinden yeni değil.');
    }
    const downloaded = await Promise.all(current.tools.map(async (pointer) =>
      readLimitedJson(await fetcher(toolUrl(currentUrl, pointer.url), {
        headers: { Accept: 'application/json' }, signal: controller.signal,
      }), maximumToolBytes)));
    const tools = await verifyTools(current, downloaded, digest);
    return {
      releaseId: current.releaseId,
      activatedAt: current.activatedAt,
      fetchedAt: now.toISOString(),
      sequence: current.sequence,
      manifest: current,
      tools,
    };
  } finally {
    clearTimeout(timer);
  }
}

function storageForBrowser(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

async function parseCache(
  raw: string | null,
  config: SignedRegistryBootstrapConfig,
): Promise<VerifiedWebRegistrySnapshot | null> {
  if (!raw) return null;
  const value: unknown = JSON.parse(raw);
  const entries = isRecord(value) ? arrayValue(value.tools) : null;
  if (!isRecord(value) || value.cacheVersion !== 1 || entries === null) {
    throw new Error('Registry web cache envelope geçersiz.');
  }
  // expiresAt imza kapsamındadır ve cache için de güvenlik sınırıdır. Ağ
  // kesintisi, süresi dolmuş kuralları sessizce yeniden yetkili kılamaz.
  const manifest = parsePointer(value.manifest, new Date(), false);
  await verifyManifest(manifest, config.keyId, config.publicKeyBase64, verifyEd25519);
  // Uygulama güncellemesi daha yeni bir paketli seed taşıyabilir. Böyle bir
  // durumda eski ama geçerli imzalı tarayıcı cache'i paketi geri saramaz.
  if (manifest.sequence <= config.bundledSequence) return null;
  const tools = await verifyTools(manifest, entries, sha256);
  return {
    releaseId: manifest.releaseId,
    activatedAt: manifest.activatedAt,
    fetchedAt: exactIsoTimestamp(value.fetchedAt, 'fetchedAt'),
    sequence: manifest.sequence,
    manifest,
    tools,
  };
}

export async function bootstrapSignedRegistry(
  config: SignedRegistryBootstrapConfig,
  bundledTools: readonly ToolDefinition[],
  storage: StorageLike | null = storageForBrowser(),
): Promise<BrowserRegistryStatus> {
  // Ağ/cache kapıları kadar paketli fallback de aynı sıra sözleşmesine tabidir;
  // bozuk bir build artefaktı güvenli fallback etiketiyle çalıştırılamaz.
  bundledTools = parseBrowserToolDefinitions(bundledTools);
  if (!config.endpoint || !config.keyId || !config.publicKeyBase64
    || !Number.isSafeInteger(config.bundledSequence) || config.bundledSequence < 0) {
    return { source: 'bundled-fallback', message: 'Uzak yayın yapılandırması eksik; güvenli paket kullanılıyor.', releaseId: null, sequence: null, tools: bundledTools };
  }
  const cacheKey = `hephesapla.registry.v1.${config.keyId}`;
  let cached: VerifiedWebRegistrySnapshot | null = null;
  let cacheFailureDetail: string | null = null;
  try {
    cached = await parseCache(storage?.getItem(cacheKey) ?? null, config);
  } catch (error) {
    cached = null;
    cacheFailureDetail = error instanceof Error ? error.message : 'bilinmeyen cache doğrulama hatası';
  }
  try {
    const snapshot = await fetchSignedRegistrySnapshot({
      endpoint: config.endpoint,
      pinnedKeyId: config.keyId,
      pinnedPublicKeyBase64: config.publicKeyBase64,
      minimumSequenceExclusive: config.bundledSequence,
      cachedSnapshot: cached,
      ...(config.allowLocalhostHttp === undefined
        ? {}
        : { allowLocalhostHttp: config.allowLocalhostHttp }),
    });
    try {
      storage?.setItem(cacheKey, JSON.stringify({
        cacheVersion: 1, fetchedAt: snapshot.fetchedAt, manifest: snapshot.manifest, tools: snapshot.tools,
      } satisfies CacheEnvelope));
    } catch {
      // Hesaplama güvenli imzalı belleği kullanabilir; kalıcılık sorunu statüde görünür.
      return { source: 'signed-remote', message: `İmzalı ortak yayın ${snapshot.releaseId} etkin; tarayıcı cache'i yazılamadı.`, releaseId: snapshot.releaseId, sequence: snapshot.sequence, tools: snapshot.tools };
    }
    return { source: 'signed-remote', message: `İmzalı ortak yayın ${snapshot.releaseId} etkin.`, releaseId: snapshot.releaseId, sequence: snapshot.sequence, tools: snapshot.tools };
  } catch (error) {
    if (cached) {
      return { source: 'signed-cache', message: `Ağ yayını doğrulanamadı; son imzalı ${cached.releaseId} yayını kullanılıyor.`, releaseId: cached.releaseId, sequence: cached.sequence, tools: cached.tools };
    }
    const remoteDetail = error instanceof Error ? error.message : 'bilinmeyen doğrulama hatası';
    const detail = cacheFailureDetail === null
      ? remoteDetail
      : `imzalı cache reddedildi: ${cacheFailureDetail}; ağ yayını: ${remoteDetail}`;
    return { source: 'bundled-fallback', message: `İmzalı yayın kullanılamadı (${detail}); güvenli paket kullanılıyor.`, releaseId: null, sequence: null, tools: bundledTools };
  }
}
